import React, { useEffect, useState } from 'react';
import Map, { Marker, NavigationControl, Popup, Source, Layer } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Building2, Navigation, Target, Activity, Filter, ChevronDown, ShieldCheck, ExternalLink, X, Plus, Check } from 'lucide-react';
import './App.css';
import AdminDashboard from './AdminDashboard.jsx';

const createGeoJSONCircle = function(center, radiusInKm, points = 64, offsetAngle = 0) {
    if (!center || !center.length) return null;
    const km = radiusInKm;

    const ret = [];
    const distanceX = km / (111.320 * Math.cos((center[1] * Math.PI) / 180));
    const distanceY = km / 110.574;

    let theta, x, y;
    for (let i = 0; i < points; i++) {
        theta = (i / points) * (2 * Math.PI) + offsetAngle;
        x = distanceX * Math.cos(theta);
        y = distanceY * Math.sin(theta);
        ret.push([center[0] + x, center[1] + y]);
    }
    ret.push(ret[0]);

    return {
        type: 'FeatureCollection',
        features: [{
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [ret]
            }
        }]
    };
};

const FILTER_TRANSLATIONS = {
    "Number of Employees": "Anzahl der Mitarbeiter",
    "Annual Revenue": "Jahresumsatz",
    "Company owner": "Zuständiger Mitarbeiter",
    "Number of Associated Deals": "Anzahl der zugeordneten Deals",
    "Postal Code": "Postleitzahl",
    "Create Date": "Erstellungsdatum"
};

export function FilterPopupContent({ activeDef, customFilters, setCustomFilters, nearbyCompanies, FILTER_TRANSLATIONS }) {
    const fState = customFilters[activeDef?.name] || {};
                     const setFState = (updates) => setCustomFilters(prev => ({...prev, [activeDef.name]: { ...prev[activeDef.name], ...updates }}));
                     let computedOptions = activeDef.options || [];
                     if (computedOptions.length === 0 && (activeDef.referencedObjectType === 'OWNER' || activeDef.name.includes('owner'))) {
                         const ownerMap = {};
                         nearbyCompanies.forEach(c => {
                             const rawValue = c.customProps?.[activeDef.name];
                             const label = activeDef.name === 'hubspot_owner_id' ? c.ownerId : (rawValue || 'Unbekannt');
                             
                             if (rawValue && rawValue !== 'Unassigned' && rawValue !== 'Unbekannt' && String(rawValue).trim() !== '') {
                                 ownerMap[rawValue] = label;
                             }
                         });
                         computedOptions = Object.entries(ownerMap)
                             .map(([val, lbl]) => ({ value: val, label: lbl }))
                             .sort((a, b) => String(a.label).localeCompare(String(b.label)));
                     }

                     return (
                         <div className="filter-group">
                             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                 <label style={{ fontSize: '13px', fontWeight: '600', color: '#86868b', display: 'block', margin: 0 }}>{FILTER_TRANSLATIONS[activeDef.label] || activeDef.label}</label>
                                 {fState.operator && (
                                     <button onClick={() => { const copy = {...customFilters}; delete copy[activeDef.name]; setCustomFilters(copy); }} style={{ background: 'none', border: 'none', color: '#ff3b30', fontSize: '13px', cursor: 'pointer', padding: 0 }}>Filter löschen</button>
                                 )}
                             </div>
                             
                             <select 
                                 value={fState.operator || ''} 
                                 onChange={(e) => setFState({ operator: e.target.value })}
                                 className="apple-select" 
                                 style={{ width: '100%', marginBottom: '10px', padding: '10px 14px', fontSize: '14px', borderRadius: '10px', border: '1px solid #d1d1d6', background: '#f2f2f7 url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2386868b%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E") no-repeat right 12px center', color: '#3a3a3c', cursor: 'pointer', outline: 'none', WebkitAppearance: 'none' }}
                             >
                                 <option value="">Operator wählen...</option>
                                 {activeDef.type === 'number' && (
                                     <>
                                         <option value="EQ">ist gleich</option>
                                         <option value="NOT_EQ">ist nicht gleich</option>
                                         <option value="BETWEEN">zwischen</option>
                                         <option value="GT">ist größer als</option>
                                         <option value="LT">ist kleiner als</option>
                                         <option value="KNOWN">ist bekannt</option>
                                         <option value="UNKNOWN">ist unbekannt</option>
                                     </>
                                 )}
                                 {activeDef.type === 'string' && (
                                     <>
                                         <option value="EQ">ist gleich</option>
                                         <option value="NOT_EQ">ist nicht gleich</option>
                                         <option value="CONTAINS">beinhaltet</option>
                                         <option value="NOT_CONTAINS">beinhaltet nicht</option>
                                         <option value="KNOWN">ist bekannt</option>
                                         <option value="UNKNOWN">ist unbekannt</option>
                                     </>
                                 )}
                                 {(activeDef.type === 'enumeration' || activeDef.type === 'boolean') && (
                                     <>
                                         <option value="IN">Auswahl von einem oder mehr</option>
                                         <option value="NOT_IN">Keine der folgenden Auswahl</option>
                                         <option value="KNOWN">ist bekannt</option>
                                         <option value="UNKNOWN">ist unbekannt</option>
                                     </>
                                 )}
                                 {(activeDef.type === 'date' || activeDef.type === 'datetime') && (
                                     <>
                                         <option value="LT">Vor</option>
                                         <option value="GT">Nach</option>
                                         <option value="BETWEEN">Zwischen</option>
                                         <option value="EQ">Am Tag</option>
                                         <option value="KNOWN">ist bekannt</option>
                                         <option value="UNKNOWN">ist unbekannt</option>
                                     </>
                                 )}
                             </select>
                             
                             {fState.operator && fState.operator !== 'KNOWN' && fState.operator !== 'UNKNOWN' && (
                                 <div style={{ marginTop: '10px' }}>
                                     {activeDef.type === 'enumeration' || activeDef.type === 'boolean' ? (
                                         <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e3e3e8', borderRadius: '10px', background: '#f5f5f7', padding: '6px' }}>
                                             {((computedOptions).concat(
                                                 (activeDef.referencedObjectType === 'OWNER' || activeDef.name.includes('owner')) 
                                                     ? [{ label: 'Unassigned', value: 'UNASSIGNED' }] 
                                                     : [{ label: 'Unbekannt', value: 'UNKNOWN' }]
                                             )).map((opt, idx) => {
                                                 const isChecked = (Array.isArray(fState.value) ? fState.value : []).includes(opt.value);
                                                 return (
                                                     <label key={opt.value + '_' + idx} style={{ display: 'flex', alignItems: 'center', padding: '4px 6px', cursor: 'pointer', borderRadius: '6px', background: isChecked ? 'rgba(0,122,255,0.1)' : 'transparent', transition: 'background 0.2s', marginBottom: '0' }}>
                                                         <input 
                                                            type="checkbox" 
                                                            value={opt.value} 
                                                            checked={isChecked}
                                                            onChange={(e) => {
                                                                let currentValues = Array.isArray(fState.value) ? [...fState.value] : [];
                                                                if (e.target.checked) currentValues.push(opt.value);
                                                                else currentValues = currentValues.filter(v => v !== opt.value);
                                                                setFState({ value: currentValues });
                                                            }}
                                                            style={{ 
                                                                marginRight: '8px', width: '14px', height: '14px', 
                                                                accentColor: '#007aff', cursor: 'pointer', 
                                                                backgroundColor: '#ffffff', borderRadius: '3px',
                                                                border: '1px solid #c7c7cc', WebkitAppearance: 'checkbox'
                                                            }}
                                                         />
                                                         <span style={{ fontSize: '13px', color: '#1d1d1f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.label}</span>
                                                     </label>
                                                 );
                                             })}
                                         </div>
                                     ) : fState.operator === 'BETWEEN' ? (
                                         <div style={{ display: 'flex', gap: '10px' }}>
                                             <input 
                                                type={activeDef.type === 'date' || activeDef.type === 'datetime' ? 'date' : 'number'}
                                                value={fState.value || ''}
                                                onChange={(e) => setFState({ value: e.target.value })}
                                                placeholder="Min..."
                                                style={{ width: '50%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e3e3e8', background: '#f5f5f7', color: '#1d1d1f', boxSizing: 'border-box', fontSize: '14px', outline: 'none' }}
                                             />
                                             <input 
                                                type={activeDef.type === 'date' || activeDef.type === 'datetime' ? 'date' : 'number'}
                                                value={fState.valueMax || ''}
                                                onChange={(e) => setFState({ valueMax: e.target.value })}
                                                placeholder="Max..."
                                                style={{ width: '50%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e3e3e8', background: '#f5f5f7', color: '#1d1d1f', boxSizing: 'border-box', fontSize: '14px', outline: 'none' }}
                                             />
                                         </div>
                                     ) : (
                                         <input 
                                             type={activeDef.type === 'date' || activeDef.type === 'datetime' ? 'date' : activeDef.type === 'number' ? 'number' : 'text'} 
                                             value={fState.value || ''} 
                                             onChange={(e) => setFState({ value: e.target.value })}
                                             placeholder="Wert eingeben..."
                                             style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e3e3e8', background: '#f5f5f7', color: '#1d1d1f', boxSizing: 'border-box', fontSize: '14px', outline: 'none' }}
                                         />
                                     )}
                                 </div>
                             )}
                         </div>
                     );
}

const MAP_STYLES = {
  light: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
  streets: "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json",
  dark: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
  satellite: {
    version: 8,
    sources: {
      "satellite-tiles": {
        type: "raster",
        tiles: [
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        ],
        tileSize: 256,
        attribution: "Tiles &copy; Esri"
      },
      "label-tiles": {
        type: "raster",
        tiles: [
          "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
        ],
        tileSize: 256
      }
    },
    layers: [
      {
        id: "satellite-layer",
        type: "raster",
        source: "satellite-tiles",
        minzoom: 0,
        maxzoom: 20
      },
      {
        id: "label-layer",
        type: "raster",
        source: "label-tiles",
        minzoom: 0,
        maxzoom: 20
      }
    ]
  }
};

const applyPresetFilter = (company, preset) => {
    if (!preset) return true;
    
    // If structured rules exist, evaluate them directly for maximum precision!
    if (preset.rules && Array.isArray(preset.rules) && preset.rules.length > 0) {
        return preset.rules.every(rule => {
            const key = rule.property;
            const op = rule.operator;
            const ruleVal = rule.value;
            
            // Get value from company
            let val = company[key];
            if (val === undefined && company.customProps) {
                val = company.customProps[key];
            }
            const safeStrVal = val !== undefined && val !== null ? String(val) : '';
            
            if (op === 'HAS_PROPERTY') return !!safeStrVal;
            if (op === 'NOT_HAS_PROPERTY') return !safeStrVal;
            
            if (op === 'EQ') {
                return safeStrVal.toLowerCase() === String(ruleVal).toLowerCase();
            }
            if (op === 'NEQ') {
                return safeStrVal.toLowerCase() !== String(ruleVal).toLowerCase();
            }
            if (op === 'CONTAINS') {
                return safeStrVal.toLowerCase().includes(String(ruleVal).toLowerCase());
            }
            
            // Numeric comparison
            const numVal = parseFloat(val);
            const numTarget = parseFloat(ruleVal);
            if (!isNaN(numVal) && !isNaN(numTarget)) {
                if (op === 'GT') return numVal > numTarget;
                if (op === 'LT') return numVal < numTarget;
            }
            return false;
        });
    }
    
    const presetDesc = typeof preset === 'string' ? preset : preset.desc;
    if (!presetDesc) return true;
    const descLower = presetDesc.toLowerCase();
    
    // Split criteria by separators like "·", ",", "and", "&", or "und"
    const criteria = descLower.split(/[·,&]|\band\b|\bund\b/).map(c => c.trim()).filter(Boolean);
    
    return criteria.every(criterion => {
        // e.g. "lifecycle = customer" or "industry = software"
        if (criterion.includes('=')) {
            const parts = criterion.split('=');
            if (parts.length === 2) {
                const key = parts[0].trim();
                const val = parts[1].trim();
                
                // Check direct fields
                if (key.includes('lifecycle') && String(company.lifecycle || '').toLowerCase() === val) return true;
                if (key.includes('industry') && String(company.industry || '').toLowerCase() === val) return true;
                if (key.includes('state') && String(company.state || '').toLowerCase() === val) return true;
                if (key.includes('country') && String(company.country || '').toLowerCase() === val) return true;
                if (key.includes('owner') && String(company.ownerId || '').toLowerCase().includes(val)) return true;
                
                // Generic check in company properties
                return Object.values(company).some(v => String(v).toLowerCase() === val) ||
                       Object.values(company.customProps || {}).some(v => String(v).toLowerCase() === val);
            }
        }
        
        // e.g. "last contact > 90d" or "revenue > 1m"
        if (criterion.includes('>')) {
            const parts = criterion.split('>');
            if (parts.length === 2) {
                const val = parts[1].trim();
                const numVal = parseFloat(val.replace(/[^0-9.]/g, ''));
                let multiplier = 1;
                if (val.includes('m') || val.includes('mio')) multiplier = 1000000;
                else if (val.includes('k')) multiplier = 1000;
                
                const target = numVal * multiplier;
                if (criterion.includes('revenue') || criterion.includes('umsatz') || val.includes('€')) {
                    return (company.annualRevenue || 0) > target;
                }
                if (criterion.includes('employees') || criterion.includes('mitarbeiter')) {
                    return (company.employees || 0) > target;
                }
                if (criterion.includes('deals')) {
                    return (company.openDeals || 0) > target;
                }
            }
        }
        
        // e.g. "has open deals" or "offene deals"
        if (criterion.includes('deals') || criterion.includes('deal')) {
            if (criterion.includes('open') || criterion.includes('offen') || criterion.includes('has')) {
                return (company.openDeals || 0) > 0;
            }
        }

        // e.g. "plz beginnt mit 7"
        if (criterion.includes('plz') || criterion.includes('zip') || criterion.includes('postleitzahl')) {
            const digits = criterion.replace(/[^0-9]/g, '');
            if (digits && company.zip) {
                return String(company.zip).startsWith(digits);
            }
        }

        // Generic text search
        return Object.values(company).some(v => String(v).toLowerCase().includes(criterion)) ||
               Object.values(company.customProps || {}).some(v => String(v).toLowerCase().includes(criterion));
    });
};

function App() {

  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [focusedCompanyIds, setFocusedCompanyIds] = useState([]);
  const [selectionMode, setSelectionMode] = useState(null); // 'segments' | 'routing'
  const [selectedIds, setSelectedIds] = useState([]);
  const [segmentSearch, setSegmentSearch] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [activePortalId, setActivePortalId] = useState(new URLSearchParams(window.location.search).get('portalId') || '');
  // Onboarding Dashboard State
  const [onboardingStats, setOnboardingStats] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [onboardingError, setOnboardingError] = useState('');
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [isInitializing, setIsInitializing] = useState(false);
  
  const [excludedCompanies, setExcludedCompanies] = useState(null);
  const [showExcluded, setShowExcluded] = useState(false);
  const [isFetchingExcluded, setIsFetchingExcluded] = useState(false);

  // Tenant Config
  const [tenantConfig, setTenantConfig] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [sessionVerified, setSessionVerified] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [presetDropdownOpen, setPresetDropdownOpen] = useState(false);
  const [addFilterDropdownOpen, setAddFilterDropdownOpen] = useState(false);
  const [moreFiltersDropdownOpen, setMoreFiltersDropdownOpen] = useState(false);

  const tipsArray = [
    "Wusstest du schon? Du kannst mit dem Lasso-Tool in der Karte gezielt Unternehmen markieren.",
    "Tipp: Navigiere zu einem Unternehmen, klicke DealPin an und lass dir die direkte Route planen.",
    "Gut zu wissen: Wir filtern deine Datenbank nach Postleitzahlen, um nur physische Standorte zu finden.",
    "Pro-Tipp: Klicke auf kleine Unternehmensgruppen (Cluster) um tiefer hineinzuzoomen.",
    "Google Maps Geocoding liefert höchste Präzision für B2B-Daten."
  ];

  useEffect(() => {
     const t = setInterval(() => setCurrentTipIndex(idx => (idx + 1) % tipsArray.length), 15000);
     return () => clearInterval(t);
  }, []);

  useEffect(() => {
      let isWelcome = currentPath === '/welcome';
      if (!isWelcome) {
          setSessionVerified(true);
          return;
      }
      
      const searchParams = new URLSearchParams(window.location.search);
      const pid = searchParams.get('portalId');
      
      if (!pid && !searchParams.get('error') && !searchParams.get('success') && !searchParams.get('code')) {
          window.location.replace('/api/oauth/install');
          return;
      }

      if (pid) {
          fetch(`/api/onboarding/verify-session?portalId=${pid}`)
              .then(r => {
                   if (r.status === 401) {
                       window.location.replace('/api/oauth/install');
                       throw new Error("Unauthorized");
                   }
                   return r.json();
              })
              .then(data => {
                   if (data && data.propertiesExist) {
                       setOnboardingStep(3);
                       fetch(`/api/onboarding/stats?portalId=${pid}`).then(res=>res.json()).then(d => {
                           if (d.success) setOnboardingStats(d.stats);
                       });
                   }
                   setSessionVerified(true);
              })
              .catch(e => {
                   console.error("Session verification failed:", e);
                   if (e.message !== "Unauthorized") {
                       setSessionVerified(true);
                   }
              });
      } else {
          setSessionVerified(true);
      }
  }, [currentPath]);

  const handleGenerateRoute = () => {
    // De-dupe coordinates to prevent stacked waypoints when clicking clusters
    const uniqueLocs = [];
    const seen = new Set();
    
    selectedIds.forEach(id => {
       const hit = markers.find(fc => fc.id === id); // Search full map state to include centerCompany
       if (hit && hit.lat) {
           const latLonKey = `${hit.lat},${hit.lon}`;
           if (!seen.has(latLonKey)) {
               seen.add(latLonKey);
               // Pre-calculate the clearest Google Maps instruction without company name
               const addressParts = [hit.address?.trim(), hit.zip?.trim(), hit.city?.trim()].filter(Boolean);
               const routingKey = addressParts.length >= 2 ? addressParts.join(', ') : latLonKey;
               uniqueLocs.push({ key: routingKey, lat: hit.lat, lon: hit.lon });
           }
       }
    });
    
    if (uniqueLocs.length === 0) return;

    // Exact TSP with DFS Pruning & Equirectangular Distance
    const getDistanceSq = (p1, p2) => {
        const avgLat = (p1.lat + p2.lat) / 2 * (Math.PI / 180);
        const dLat = p1.lat - p2.lat;
        const dLon = (p1.lon - p2.lon) * Math.cos(avgLat);
        return dLat * dLat + dLon * dLon;
    };

    let sortedLocs = [];
    
    if (uniqueLocs.length <= 1) {
        sortedLocs = [...uniqueLocs];
    } else {
        const origin = uniqueLocs[0];
        const toVisit = uniqueLocs.slice(1);
        
        if (toVisit.length > 10) {
            // Fallback: Nearest Neighbor for large sets
            sortedLocs = [origin];
            let current = origin;
            const unvisited = [...toVisit];
            while (unvisited.length > 0) {
                let nearestIdx = 0, minDistance = Infinity;
                for (let i = 0; i < unvisited.length; i++) {
                    const d = getDistanceSq(current, unvisited[i]);
                    if (d < minDistance) { minDistance = d; nearestIdx = i; }
                }
                current = unvisited.splice(nearestIdx, 1)[0];
                sortedLocs.push(current);
            }
        } else {
            // Exact TSP via DFS Pruning
            let minTotalDist = Infinity;
            let bestOrder = [];
            const n = toVisit.length;
            const visited = new Array(n).fill(false);
            const currentPath = new Array(n);

            const dfs = (depth, lastNode, currentDist) => {
                if (currentDist >= minTotalDist) return; // Branch Pruning
                if (depth === n) {
                    minTotalDist = currentDist;
                    bestOrder = [...currentPath];
                    return;
                }
                for (let i = 0; i < n; i++) {
                    if (!visited[i]) {
                        visited[i] = true;
                        currentPath[depth] = toVisit[i];
                        dfs(depth + 1, toVisit[i], currentDist + getDistanceSq(lastNode, toVisit[i]));
                        visited[i] = false;
                    }
                }
            };
            
            dfs(0, origin, 0);
            sortedLocs = [origin, ...bestOrder];
        }
    }

    const finalKeys = sortedLocs.map(loc => loc.key);

    const url = new URL('https://www.google.com/maps/dir/');
    url.searchParams.append('api', '1');
    
    if (finalKeys.length === 1) {
        url.searchParams.append('destination', finalKeys[0]);
    } else {
        url.searchParams.append('origin', finalKeys[0]);
        url.searchParams.append('destination', finalKeys[finalKeys.length - 1]);
        
        if (finalKeys.length > 2) {
            url.searchParams.append('waypoints', finalKeys.slice(1, -1).join('|'));
        }
    }
    window.open(url.toString(), '_blank');
  };

  const [availableLists, setAvailableLists] = useState([]);
  const [toast, setToast] = useState(null);


  const showToast = (message, type = 'success') => {
      setToast({ message, type });
      setTimeout(() => setToast(null), 3500);
  };
  
  useEffect(() => {
    if (selectionMode === 'segments') {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token') || '';
      const headers = {};
      if (token) {
          headers['Authorization'] = `Bearer ${token}`;
      }
      fetch('/api/lists?portalId=' + activePortalId, { headers })
        .then(res => res.json())
        .then(data => { if(data.success && data.lists) setAvailableLists(data.lists); });
    }
  }, [selectionMode, activePortalId]);

  const addCompaniesToSegment = async (listId) => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token') || '';
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
          headers['Authorization'] = `Bearer ${token}`;
      }
      try {
          const res = await fetch('/api/lists/add', {
              method: 'POST', headers: headers,
              body: JSON.stringify({ portalId: activePortalId, listId: listId, recordIds: selectedIds })
          });
          const data = await res.json();
          if(data.success) { 
              showToast('Unternehmen erfolgreich zum Segment hinzugefügt!', 'success'); 
              setSelectionMode(null); 
              setSelectedIds([]); 
          }
          else { showToast('Fehler: ' + (data.error || 'Server-Abbruch'), 'error'); }
      } catch (e) { showToast('Netzwerkfehler', 'error'); }
  };

  const createNewSegment = async (suggestedName) => {
      const segmentName = suggestedName || prompt('Name für neues Segment:');
      if (!segmentName) return;
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token') || '';
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
          headers['Authorization'] = `Bearer ${token}`;
      }
      try {
          const res = await fetch('/api/lists', {
              method: 'POST', headers: headers,
              body: JSON.stringify({ portalId: activePortalId, name: segmentName })
          });
          const data = await res.json();
          
          let parsedListId = data?.list?.listId || data?.list?.list?.listId || data?.list?.id;
          
          if(res.ok && data.success && parsedListId) {
              addCompaniesToSegment(parsedListId);
          } else { 
              showToast('Erstellung fehlgeschlagen: ' + (data.error || 'Ungültige Rückgabe'), 'error'); 
          }
      } catch (e) { showToast('Netzwerkfehler', 'error'); }
  };

  // Parse URL parameters for initial coordinates & nearby payload
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lat = params.get('lat');
    const lon = params.get('lon');
    const name = params.get('name') || 'Aktuelles Unternehmen';
    const address = params.get('address') || '';
    const zip = params.get('zip') || '';
    const city = params.get('city') || '';
    const country = params.get('country') || '';
    const domain = params.get('domain') || '';
    
    const annualRevenue = parseFloat(params.get('revenue') || 0);
    const employees = parseInt(params.get('employees') || 0, 10);
    const ownerId = params.get('ownerId') || 'Unassigned';
    const teamId = params.get('teamId') || 'No Team';
    const openDeals = parseInt(params.get('openDeals') || 0, 10);
    const closedDeals = parseInt(params.get('closedDeals') || 0, 10);
    const totalDealValue = 0; // Fallback placeholder if not passed
    
    // Fallback parsing for nearby companies passed via URL
    const nearbyRaw = params.get('nearby');

    let parsedMarkers = [];

    // Only attempt to load CRM payload properties if we are NOT on the welcome page
    if (currentPath === '/welcome') {
         return;
    }

    const portalId = params.get('portalId') || '';
    const token = params.get('token') || '';
    const userId = params.get('userId') || '';

    // Check configuration and Access Controls before allowing the map
    const isScreenshot = params.get('screenshot_mode') === 'true';
    if (currentPath !== '/admin' && !isScreenshot) {
        const headers = { 'Content-Type': 'application/json' };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        fetch(`/api/admin/config?portalId=${portalId}`, { headers })
            .then(res => res.json())
            .then(data => {
                if (data.success && data.config) {
                    if (data.portalId) setActivePortalId(data.portalId);
                    if (data.config.active_filters) {
                         data.config.active_filters = data.config.active_filters.map(f => {
                              if (typeof f === 'string') return { name: f, label: f, type: 'string' };
                              return f;
                         });
                    }
                    setTenantConfig(data.config);
                    
                    const allowedUsers = data.config.allowed_users || [];
                    const userRoles = data.config.user_roles || {};
                    const autoUserAccess = data.config.auto_user_access !== undefined ? data.config.auto_user_access : true;
                    
                    if (userId) {
                        const uIdStr = String(userId);
                        const role = userRoles[uIdStr] || (allowedUsers.includes(uIdStr) ? 'user' : (autoUserAccess ? 'user' : 'none'));
                        if (role === 'none' || role === 'blocked') {
                            setAccessDenied(true);
                            return; // Halt map rendering
                        }
                    }
                }
            })
            .catch(() => {});
    }

    // 1. Add Center Company Marker
    if (lat && lon) {
      parsedMarkers.push({ 
        id: params.get('id') || 'initial', lat: parseFloat(lat), lon: parseFloat(lon), name, address, zip, country, domain,
        annualRevenue, employees, ownerId, teamId, openDeals, closedDeals, totalDealValue, isCenter: true 
      });
      setMarkers([...parsedMarkers]);
    } else if (zip && country) {
      // We natively geocode the real address upfront so the map starts accurately!
      const addressQuery = [address, zip, city, country].filter(Boolean).join(', ');
      
      fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressQuery)}&limit=1`)
          .then(res => res.json())
          .then(geoData => {
              let finalLat = 51.1657; // safe fallback
              let finalLon = 10.4515;
              if (geoData && geoData.length > 0) {
                  finalLat = parseFloat(geoData[0].lat);
                  finalLon = parseFloat(geoData[0].lon);
              }
              
              setMarkers([{ 
                id: params.get('id') || 'initial', lat: finalLat, lon: finalLon, name, address, zip, country, domain,
                annualRevenue, employees, ownerId, teamId, openDeals, closedDeals, totalDealValue, isCenter: true 
              }]);
              
              // Removed auto-trigger based on user feedback
          })
          .catch(e => {
              console.error("Geocoding failed", e);
          });
    } else {
       const recordId = params.get('id');
       let recordType = params.get('type');
       if (!recordType || recordType === 'undefined' || recordType === 'null') {
           recordType = 'COMPANY';
       }
       const portalId = params.get('portalId') || '';

       if (recordId) {
            const token = params.get('token') || '';
            const headers = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            fetch('/api/get-record-address', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({ id: recordId, type: recordType, portalId })
            })
           .then(async r => {
               const res = await r.json();
               if (!r.ok || !res.success) throw res;
               return res;
           })
           .then(res => {
               if (res.success && res.properties) {
                    const p = res.properties;
                    const rName = p.name || p.dealname || 'Unbekannt';
                    const rAddress = p.address || '';
                    const rZip = p.zip || '';
                    const rCity = p.city || '';
                    const rCountry = p.country || 'Germany';
                    
                    const pRevenue = p.annualrevenue ? parseInt(p.annualrevenue) : (p.amount ? parseInt(p.amount) : 0);
                    const pEmployees = p.numberofemployees ? parseInt(p.numberofemployees) : 0;
                    const pOwnerId = p.ownername || p.hubspot_owner_name || p.hubspot_owner_id || '';
                    const pTeamId = p.teamname || p.hubspot_team_id || '';
                    const pOpenDeals = p.num_associated_deals ? parseInt(p.num_associated_deals) : 0;

                    // Update URL silently so handleLoadNearby gets the exact variables later!
                    const newUrl = new URL(window.location);
                    newUrl.searchParams.set('name', rName);
                    newUrl.searchParams.set('address', rAddress);
                    newUrl.searchParams.set('zip', rZip);
                    newUrl.searchParams.set('city', rCity);
                    newUrl.searchParams.set('country', rCountry);
                    window.history.replaceState({}, '', newUrl);

                    const finalAddressQuery = [rAddress, rZip, rCity, rCountry].filter(Boolean).join(', ');
                    
                    const openMapWithCoords = (fLat, fLon) => {
                        setMarkers([{ 
                            id: recordId, lat: fLat, lon: fLon, name: rName, address: rAddress, zip: rZip, country: rCountry, domain,
                            annualRevenue: pRevenue || annualRevenue, 
                            employees: pEmployees || employees, 
                            ownerId: pOwnerId || ownerId, 
                            teamId: pTeamId || teamId, 
                            openDeals: pOpenDeals || openDeals, 
                            closedDeals, totalDealValue, isCenter: true 
                        }]);
                    };

                    const nLat = parseFloat(p.dealpin_lat);
                    const nLon = parseFloat(p.dealpin_lon);

                    if (!isNaN(nLat) && !isNaN(nLon) && nLat !== 0 && nLon !== 0 && nLat !== -1 && nLon !== -1) {
                        openMapWithCoords(nLat, nLon);
                    } else {
                        fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(finalAddressQuery)}&limit=1`)
                            .then(n => n.json())
                            .then(geoData => {
                                let fLat = 51.1657; let fLon = 10.4515;
                                if (geoData && geoData.length > 0) { fLat = parseFloat(geoData[0].lat); fLon = parseFloat(geoData[0].lon); }
                                openMapWithCoords(fLat, fLon);
                            }).catch(() => openMapWithCoords(51.1657, 10.4515));
                    }
                    
                    // Removed auto-trigger search on load
               } else {
                   setError(`Fehler: ${res.message || 'Unbekanntes Problem'}`);
               }
           })
           .catch((e) => {
               console.error(e);
               let errorDetails = e.message || '';
               if (e.hubspotError) {
                   errorDetails += ' | HubSpot API: ' + (typeof e.hubspotError === 'object' ? JSON.stringify(e.hubspotError) : e.hubspotError);
               }
               setError("Backend Error: " + (errorDetails || "Netzwerkfehler"));
           });
       }
    }

    // 2. Add Nearby Companies Payload
    if (nearbyRaw) {
      try {
        const parsed = JSON.parse(nearbyRaw);
        if (Array.isArray(parsed)) {
          parsedMarkers = [...parsedMarkers, ...parsed.map(n => ({
             ...n,
             isCenter: false
          }))];
        }
      } catch (e) {
        console.error("Fehler beim Parsen von Nearby-Daten aus der URL", e);
      }
    }

    setMarkers(parsedMarkers);
  }, []);

  // Search Parameters
  const [searchMode, setSearchMode] = useState('radius'); // 'radius' or 'zip'
  const [radius, setRadius] = useState(10); // km
  const [zipLength, setZipLength] = useState(2); // 1 to 5
  const [isSearching, setIsSearching] = useState(false); // Controls Two-State UI
  const [activeFilter, setActiveFilter] = useState(null);
  const [filters, setFilters] = useState({
      minRevenue: 0,
      minEmployees: 0,
      ownerId: '',
      teamId: '',
      minOpenDeals: 0,
      minClosedDeals: 0,
      minDealValue: 0
  });
  const [customFilters, setCustomFilters] = useState({});
  const [popupStyle, setPopupStyle] = useState({});
  const [anchorBtnId, setAnchorBtnId] = useState(null);

  useEffect(() => {
     if (!activeFilter) {
         setAnchorBtnId(null);
         return;
     }

     // Determine the anchor button at the moment the filter becomes active
     let id = 'filter-btn-add';
     if (document.getElementById('filter-btn-' + activeFilter)) {
         id = 'filter-btn-' + activeFilter;
     } else {
         // Check if the filter is one of the collapsed ones
         const activeFilterDefs = (tenantConfig?.active_filters || []).filter(filterDef => {
             const fState = customFilters[filterDef.name];
             return !!fState?.operator;
         });
         const showAllDirectly = activeFilterDefs.length <= 3;
         const collapsedFilters = showAllDirectly ? [] : activeFilterDefs.slice(2);
         const isCollapsed = collapsedFilters.some(f => f.name === activeFilter);
         
         if (isCollapsed && document.getElementById('filter-btn-more')) {
             id = 'filter-btn-more';
         }
     }
     setAnchorBtnId(id);
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  useEffect(() => {
     if (anchorBtnId) {
        const updatePosition = () => {
            const btn = document.getElementById(anchorBtnId);
            if (btn) {
               const rect = btn.getBoundingClientRect();
               let leftPos = rect.left + (rect.width / 2);
               let transform = 'translateX(-50%)';
               
               if (leftPos < 180) { leftPos = 20; transform = 'none'; }
               else if (window.innerWidth - leftPos < 180) { leftPos = window.innerWidth - 380; transform = 'none'; }
               
               setPopupStyle({
                  position: 'fixed',
                  top: (rect.bottom + 12) + 'px',
                  left: leftPos + 'px',
                  transform: transform,
                  zIndex: 100
               });
            }
        };
        
        // Update immediately
        updatePosition();
        
        // Also update after a short delay to allow layout adjustments
        const timer = setTimeout(updatePosition, 50);
        
        window.addEventListener('resize', updatePosition);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', updatePosition);
        };
     }
  }, [anchorBtnId, customFilters]);

  const togglePresetDropdown = () => {
      setPresetDropdownOpen(!presetDropdownOpen);
      setAddFilterDropdownOpen(false);
      setActiveFilter(null);
      setMoreFiltersDropdownOpen(false);
  };

  const toggleAddFilterDropdown = () => {
      setAddFilterDropdownOpen(!addFilterDropdownOpen);
      setPresetDropdownOpen(false);
      setActiveFilter(null);
      setMoreFiltersDropdownOpen(false);
  };

  const toggleActiveFilter = (name) => {
      setActiveFilter(activeFilter === name ? null : name);
      setPresetDropdownOpen(false);
      setAddFilterDropdownOpen(false);
      setMoreFiltersDropdownOpen(false);
  };

  const toggleMoreFiltersDropdown = () => {
      setMoreFiltersDropdownOpen(!moreFiltersDropdownOpen);
      setPresetDropdownOpen(false);
      setAddFilterDropdownOpen(false);
      setActiveFilter(null);
  };

  const closeAllDropdowns = () => {
      setActiveFilter(null);
      setPresetDropdownOpen(false);
      setAddFilterDropdownOpen(false);
      setMoreFiltersDropdownOpen(false);
  };

  const [circleRotation, setCircleRotation] = useState(0);

  useEffect(() => {
     let timeoutId;
     let currentRot = 0;
     const rotate = () => {
         currentRot -= 0.002; // Very slow and elegant rotation (10% of previous speed)
         setCircleRotation(currentRot);
         timeoutId = setTimeout(rotate, 50); // 20fps prevents React reconciliation lag
     }
     if (loading && searchMode === 'radius') {
         timeoutId = setTimeout(rotate, 50);
     } else {
         setCircleRotation(0);
     }
     return () => {
         if (timeoutId) clearTimeout(timeoutId);
     };
  }, [loading, searchMode]);
  // Global URL Params Parsing
  const queryParams = new URLSearchParams(window.location.search);
  const isScreenshotMode = queryParams.get('screenshot_mode') === 'true';

  useEffect(() => {
     if (isScreenshotMode) {
        document.body.style.margin = '0';
        document.body.style.padding = '0';
        document.body.style.overflow = 'hidden';
        const root = document.getElementById('root');
        if (root) {
            root.style.setProperty('width', '1200px', 'important');
            root.style.setProperty('max-width', '1200px', 'important');
            root.style.setProperty('height', '600px', 'important');
            root.style.setProperty('margin', '0', 'important');
            root.style.setProperty('padding', '0', 'important');
            root.style.setProperty('border', 'none', 'important');
        }
     }
  }, [isScreenshotMode]);

  const handleLoadNearby = async () => {
    const params = new URLSearchParams(window.location.search);
    const zip = params.get('zip');
    const country = params.get('country');
    const token = params.get('token');
    const objectId = params.get('id');
    const name = params.get('name');
    const urlLat = params.get('lat');
    const urlLon = params.get('lon');

    // Puppeteer Background Render Bypass
    if (isScreenshotMode) {
        setIsSearching(true);
        if (urlLat && urlLon) {
             setMarkers([{ name: name || '', zip: zip || '', country: country || '', lat: parseFloat(urlLat), lon: parseFloat(urlLon), isCenter: true }]);
        }
        return;
    }

    if (!zip || !country) {
       setError("Fehler: PLZ und Land fehlen für diesen Standort.");
       return;
    }

    setLoading(true);
    setError(null);

    const initialCenter = markers.find(m => m.isCenter) || markers[0];

    try {
        const headers = { 'Content-Type': 'application/json' };
        if (token) {
             headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch('/api/get-nearby-companies', {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({ 
                zip, 
                country, 
                objectId, 
                centerLat: initialCenter.lat, 
                centerLon: initialCenter.lon,
                searchMode,
                radius,
                zipLength, // NEW: Dynamic zip depth!
                portalId: params.get('portalId') || '', // required for dynamic filters
                userId: params.get('userId') || ''
            })
        });

        if (!response.ok) {
           throw new Error(`Backend Request failed with status ${response.status}`);
        }

        const result = await response.json();
        
        if (result.success && result.data && result.data.length > 0) {
           setMarkers(prev => {
                const center = prev.find(m => m.isCenter) || prev[0];
                return [center, ...result.data];
           });
           setFocusedCompanyIds([]); // Clear active focus to see new map
           setIsSearching(true); // Toggle the UI to Results View
           
           // Activity Logging Background Task
           if (token && tenantConfig && tenantConfig.activity_logging_enabled && objectId) {
               fetch('/api/log-activity', {
                   method: 'POST',
                   headers: headers,
                   body: JSON.stringify({
                       portalId: params.get('portalId'),
                       companyId: objectId,
                       text: searchMode === 'radius' ? `Umkreissuche (${radius}km) gestartet` : `PLZ-Gebietssuche (${zipLength}-stellig) gestartet`
                   })
               }).catch(e => console.error("Logging failed", e));
           }
        } else {
           setError("Keine benachbarten Unternehmen " + (searchMode==='radius' ? `im ${radius}km Umkreis ` : 'im PLZ-Gebiet ') + "gefunden.");
           setTimeout(() => setError(null), 5000);
        }

    } catch (err) {
        console.error("Load Nearby Error:", err);
        setError("Fehler beim Abrufen der Nachbarn vom Backend.");
        setTimeout(() => setError(null), 5000);
    } finally {
        setLoading(false);
    }
  };



  const hasError = queryParams.get('error') === 'true';

  const fetchOnboardingStats = async () => {
      if (!activePortalId) {
          setOnboardingError("Fehlende Portal-ID. Bitte über HubSpot öffnen.");
          return;
      }
      try {
          const r = await fetch(`/api/onboarding/stats?portalId=${activePortalId}`);
          if (r.status === 401) {
              window.location.replace('/api/oauth/install');
              return;
          }
          const d = await r.json();
          if (d.success) setOnboardingStats(d.stats);
          else setOnboardingError(d.message);
      } catch(e) { setOnboardingError("Netzwerkfehler"); }
  };

  const fetchExcluded = async () => {
      setIsFetchingExcluded(true);
      try {
          const r = await fetch(`/api/onboarding/debug-excluded?portalId=${activePortalId}`);
          const d = await r.json();
          if (d.success) {
              setExcludedCompanies(d.companies);
              setShowExcluded(true);
          }
      } catch(e) {}
      setIsFetchingExcluded(false);
  };

  const startBatchSync = async () => {
      setIsSyncing(true);
      setOnboardingError(''); // clear previous errors
      let isDone = false;
      let afterCursor = null;
      while (!isDone) {
          try {
              const r = await fetch('/api/onboarding/process-batch', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ portalId: activePortalId, after: afterCursor })
              });
              const d = await r.json();
              if (d.success) {
                  if (!d.after) {
                      isDone = true;
                  }
                  afterCursor = d.after || null;
                  
                  // Update pending count locally to update UI responsively
                  setOnboardingStats(prev => prev ? {
                      ...prev,
                      pending: Math.max(0, prev.pending - d.processed)
                  } : null);
              } else {
                  setOnboardingError(d.message);
                  isDone = true;
              }
          } catch(e) {
              setOnboardingError("Netzwerkfehler");
              isDone = true;
          }
          if (!isDone) {
              await new Promise(resolve => setTimeout(resolve, 800)); // 800ms throttle between batches
          }
      }
      // Fetch final stats when done to verify and update UI with exact figures
      await fetchOnboardingStats();
      setIsSyncing(false);
  };

  if (currentPath === '/welcome') {
      const searchParams = new URLSearchParams(window.location.search);
      // If none of the OAuth/App context parameters are present, force re-auth
      if (!searchParams.get('portalId') && !searchParams.get('error') && !searchParams.get('success') && !searchParams.get('code')) {
          return null;
      }
      
      // Block rendering until backend verifies the cryptographic session
      if (!sessionVerified) {
          return (
              <div style={{ width: '100vw', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                 <div className="loader" style={{ width: '40px', height: '40px' }}></div>
              </div>
          );
      }
      
      const pendingCount = onboardingStats?.pending || 0;
      const isComplete = onboardingStats && onboardingStats.total > 0 && pendingCount === 0;
      
      const triggerInit = () => {
          setIsInitializing(true);
          setOnboardingError('');
          fetch('/api/onboarding/init', { 
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ portalId: activePortalId })
          })
             .then(r=>r.json())
             .then(d => {
                 if (d.success) {
                     setOnboardingStep(3);
                     fetchOnboardingStats(); // Fetch the stats immediately for Step 3
                 } else setOnboardingError(d.message);
                 setIsInitializing(false);
             })
             .catch(() => {
                 setOnboardingError("Netzwerkfehler");
                 setIsInitializing(false);
             });
      };

      // Estimated time calculation: Each batch of 50 items takes approximately 6.8 seconds (sequential geocoding + network + 800ms throttle)
      const estTimeMinutes = Math.ceil((pendingCount / 50) * 6.8 / 60);

      // --- Split Screen Layout ---
      return (
          <div style={{ width: '100vw', height: '100vh', display: 'flex', background: '#ffffff', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
              
              {/* LEFT NAVIGATION PANEL */}
              <div style={{ width: '40%', minWidth: '400px', background: '#f8f8f9', borderRight: '1px solid #e5e5ea', padding: '60px', display: 'flex', flexDirection: 'column' }}>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '80px' }}>
                      <img src="/Deal-Pin-Logo-Long.png" alt="DealPin Logo" style={{ height: '40px', maxWidth: '100%', objectFit: 'contain' }} />
                  </div>

                  <h1 style={{ fontSize: '32px', fontWeight: '800', lineHeight: '1.2', color: '#1d1d1f', marginBottom: '16px', letterSpacing: '-1px', textAlign: 'center' }}>
                      Einrichtung in 4 Schritten.
                  </h1>
                  <p style={{ fontSize: '17px', color: '#86868b', marginBottom: '60px', lineHeight: '1.5', textAlign: 'center' }}>
                      Wir konfigurieren dein HubSpot Portal für die intelligente GPS-Suche.
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '30px', flex: 1, margin: '0 auto', width: 'fit-content', textAlign: 'left' }}>
                     {/* Step 1 Indicator */}
                     <div style={{ display: 'flex', opacity: onboardingStep === 1 ? 1 : 0.4, transition: '0.3s' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: onboardingStep > 1 ? '#34c759' : (onboardingStep === 1 ? '#007aff' : '#e5e5ea'), color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', marginRight: '16px' }}>
                           {onboardingStep > 1 ? '✓' : '1'}
                        </div>
                        <div>
                           <div style={{ fontWeight: '600', fontSize: '17px', color: '#1d1d1f' }}>HubSpot Autorisierung</div>
                           {onboardingStep === 1 && <div style={{ fontSize: '15px', color: '#86868b', marginTop: '4px' }}>Verbindung wird hergestellt</div>}
                        </div>
                     </div>

                     {/* Step 2 Indicator */}
                     <div style={{ display: 'flex', opacity: onboardingStep === 2 ? 1 : 0.4, transition: '0.3s' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: onboardingStep > 2 ? '#34c759' : (onboardingStep === 2 ? '#007aff' : '#e5e5ea'), color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', marginRight: '16px' }}>
                           {onboardingStep > 2 ? '✓' : '2'}
                        </div>
                        <div>
                           <div style={{ fontWeight: '600', fontSize: '17px', color: '#1d1d1f' }}>Datenbank Eigenschaften</div>
                           {onboardingStep === 2 && <div style={{ fontSize: '15px', color: '#86868b', marginTop: '4px' }}>GPS-Felder anlegen</div>}
                        </div>
                     </div>

                     {/* Step 3 Indicator */}
                     <div style={{ display: 'flex', opacity: onboardingStep === 3 ? 1 : 0.4, transition: '0.3s' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: onboardingStep > 3 ? '#34c759' : (onboardingStep === 3 ? '#007aff' : '#e5e5ea'), color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', marginRight: '16px' }}>
                           {onboardingStep > 3 ? '✓' : '3'}
                        </div>
                        <div>
                           <div style={{ fontWeight: '600', fontSize: '17px', color: '#1d1d1f' }}>Bestandsanalyse</div>
                           {onboardingStep === 3 && <div style={{ fontSize: '15px', color: '#86868b', marginTop: '4px' }}>Unternehmen werden gefiltert</div>}
                        </div>
                     </div>

                     {/* Step 4 Indicator */}
                     <div style={{ display: 'flex', opacity: onboardingStep === 4 ? 1 : 0.4, transition: '0.3s' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: onboardingStep > 4 ? '#34c759' : (onboardingStep === 4 ? '#007aff' : '#e5e5ea'), color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', marginRight: '16px' }}>
                           {isComplete ? '✓' : '4'}
                        </div>
                        <div>
                           <div style={{ fontWeight: '600', fontSize: '17px', color: '#1d1d1f' }}>Standort-Berechnung</div>
                           {onboardingStep === 4 && <div style={{ fontSize: '15px', color: '#86868b', marginTop: '4px' }}>Google Maps Batching</div>}
                        </div>
                     </div>
                  </div>
              </div>

              {/* RIGHT ACTION PANEL */}
              <div style={{ flex: '1 1 60%', padding: '60px', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                  
                  {/* Top Progress Bar (Visible only when Processing) */}
                  {onboardingStep === 4 && isSyncing && onboardingStats && (
                     <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: '#e5e5ea' }}>
                        <div style={{ height: '100%', background: '#007aff', width: `${((onboardingStats.total - onboardingStats.pending) / Math.max(1, onboardingStats.total)) * 100}%`, transition: 'width 1s ease' }} />
                     </div>
                  )}

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: '420px', margin: '0 auto', width: '100%' }}>
                      {/* Step 1 Content */}
                      {onboardingStep === 1 && (
                          <div style={{ animation: 'fadeIn 0.5s', textAlign: 'center' }}>
                              <div style={{ background: '#e4f8ea', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 30px auto' }}>
                                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#34C759" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                              </div>
                              <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px' }}>HubSpot verbunden</h2>
                              <p style={{ color: '#86868b', marginBottom: '40px', lineHeight: '1.5' }}>Dein Nutzerkonto wurde erfolgreich autorisiert. Wir können nun Daten synchronisieren.</p>
                              <button onClick={() => setOnboardingStep(2)} style={{ width: '100%', boxSizing: 'border-box', padding: '18px', background: '#007aff', color: 'white', border: 'none', borderRadius: '14px', fontSize: '17px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', overflow: 'hidden', outline: 'none' }} onMouseOver={e => e.currentTarget.style.opacity = '0.8'} onMouseOut={e => e.currentTarget.style.opacity = '1'}>
                                 Schritt 2 starten
                              </button>
                          </div>
                      )}

                      {/* Step 2 Content */}
                      {onboardingStep === 2 && (
                          <div style={{ animation: 'fadeIn 0.5s', textAlign: 'center' }}>
                              <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '16px' }}>Eigenschaften anlegen</h2>
                              <p style={{ color: '#86868b', marginBottom: '24px', lineHeight: '1.5' }}>
                                  Wir erstellen nun die Eigenschaften <b>dealpin_lat</b> und <b>dealpin_lon</b> in deinem Unternehmensobjekt, um die künftigen Koordinaten speichern zu können.
                              </p>
                              {onboardingError && <div style={{ color: '#ff3b30', background: '#ffe5e5', padding: '12px', borderRadius: '12px', marginBottom: '20px', fontSize: '14px' }}>{onboardingError}</div>}
                              <button onClick={triggerInit} disabled={isInitializing} style={{ width: '100%', boxSizing: 'border-box', padding: '18px', background: isInitializing ? '#e5e5ea' : '#007aff', color: isInitializing ? '#86868b' : 'white', border: 'none', borderRadius: '14px', fontSize: '17px', fontWeight: '600', cursor: isInitializing ? 'not-allowed' : 'pointer', transition: 'all 0.2s', position: 'relative', overflow: 'hidden', outline: 'none' }} onMouseOver={e => !isInitializing && (e.currentTarget.style.opacity = '0.8')} onMouseOut={e => !isInitializing && (e.currentTarget.style.opacity = '1')}>
                                 {isInitializing ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                       <div className="loader" style={{ width: '18px', height: '18px', marginBottom: 0, border: '3px solid rgba(134,134,139,0.3)', borderTopColor: '#86868b' }}></div>
                                       Generiere Eigenschaften...
                                    </div>
                                 ) : 'Eigenschaften generieren'}
                              </button>
                          </div>
                      )}

                      {/* Step 3 Content */}
                      {onboardingStep === 3 && (
                          <div style={{ animation: 'fadeIn 0.5s' }}>
                              {!onboardingStats ? (
                                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#86868b' }}>
                                     <div className="loader" style={{ margin: '0 auto 20px auto' }}></div>
                                     Analysiere Datensätze...
                                  </div>
                              ) : (
                                  <>
                                      <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px', textAlign: 'center' }}>Analyse abgeschlossen</h2>
                                      <div style={{ background: '#f5f5f7', padding: '24px', borderRadius: '16px', marginBottom: '30px' }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '12px', borderBottom: '1px solid #e5e5ea', marginBottom: '12px' }}>
                                              <span style={{ color: '#555' }}>Unternehmen (Total)</span>
                                              <span style={{ fontWeight: 600 }}>{onboardingStats.total}</span>
                                          </div>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '12px', borderBottom: '1px solid #e5e5ea', marginBottom: '12px' }}>
                                              <span style={{ color: '#555' }}>Standorte (DE + PLZ)</span>
                                              <span style={{ fontWeight: 600 }}>{onboardingStats.deTotal}</span>
                                          </div>
                                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                              <span style={{ color: '#555' }}>Noch zu geocodieren</span>
                                              <span style={{ fontWeight: 700, color: '#FF9500' }}>{onboardingStats.pending}</span>
                                          </div>
                                      </div>
                                      <button onClick={() => setOnboardingStep(4)} style={{ width: '100%', boxSizing: 'border-box', padding: '18px', background: '#007aff', color: 'white', border: 'none', borderRadius: '14px', fontSize: '17px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', overflow: 'hidden', outline: 'none' }} onMouseOver={e => e.currentTarget.style.opacity = '0.8'} onMouseOut={e => e.currentTarget.style.opacity = '1'}>
                                          Bestätigen & Weiter
                                      </button>

                                      {(onboardingStats.total - onboardingStats.deTotal > 0) && (
                                          <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                              <button onClick={fetchExcluded} disabled={isFetchingExcluded} style={{ background: 'transparent', border: 'none', color: '#007aff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }} onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'} onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}>
                                                  {isFetchingExcluded ? 'Lade Daten...' : `${onboardingStats.total - onboardingStats.deTotal} nicht gematchte Unternehmen anzeigen`}
                                              </button>
                                          </div>
                                      )}

                                      {showExcluded && excludedCompanies && (
                                          <div style={{ marginTop: '16px', background: '#ffe5e5', borderRadius: '12px', padding: '16px', maxHeight: '200px', overflowY: 'auto', textAlign: 'left', fontSize: '13px', color: '#1d1d1f', border: '1px solid #ffb3b3' }}>
                                             <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#ff3b30' }}>Fehlende PLZ / Nicht DE:</h4>
                                             {excludedCompanies.map(c => (
                                                 <div key={c.id} style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                                                     <strong>{c.properties.name || `ID: ${c.id}`}</strong>
                                                     <div style={{ color: '#86868b', marginTop: '2px' }}>Land: {c.properties.country || '-'}, PLZ: {c.properties.zip || '-'}</div>
                                                 </div>
                                             ))}
                                             {excludedCompanies.length === 100 && <div style={{ color: '#ff3b30', marginTop: '10px', textAlign: 'center', fontWeight: 'bold' }}>Max. 100 Ergebnisse angezeigt.</div>}
                                          </div>
                                      )}
                                  </>
                              )}
                          </div>
                      )}

                      {/* Step 4 Content */}
                      {onboardingStep === 4 && (
                          <div style={{ animation: 'fadeIn 0.5s', textAlign: 'center' }}>
                             {isComplete ? (
                                  <div style={{ padding: '20px 0' }}>
                                      <div style={{ background: '#e4f8ea', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px auto' }}>
                                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#34C759" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                      </div>
                                      <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '12px' }}>Startklar!</h2>
                                      <p style={{ color: '#86868b', lineHeight: '1.5' }}>Alle vorhandenen Adressen wurden erfolgreich per GPS erfasst. Du kannst diese Seite nun schließen und DealPin in deinen HubSpot CRM Karten (auf Firmenebene) nutzen.</p>
                                  </div>
                             ) : (
                                  <>
                                      <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px' }}>High-Speed Geocoding</h2>
                                      <p style={{ color: '#86868b', marginBottom: '40px', lineHeight: '1.5' }}>Wir senden deine {onboardingStats?.pending || 0} ausstehenden Datensätze an Google Maps. Dieser Schritt läuft als paralleler Batch-Prozess.</p>
                                      
                                      <div style={{ display: 'flex', gap: '20px', marginBottom: '40px' }}>
                                         <div style={{ flex: 1, padding: '20px', background: '#f5f5f7', borderRadius: '16px' }}>
                                            <div style={{ fontSize: '32px', fontWeight: '800', color: isSyncing ? '#007aff' : '#1d1d1f', marginBottom: '4px' }}>
                                                {onboardingStats?.pending || 0}
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#86868b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Warteschlange</div>
                                         </div>
                                         <div style={{ flex: 1, padding: '20px', background: '#f5f5f7', borderRadius: '16px' }}>
                                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#1d1d1f', marginBottom: '4px' }}>
                                                {estTimeMinutes < 1 ? '< 1' : estTimeMinutes}
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#86868b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Minuten</div>
                                         </div>
                                      </div>
                                      
                                      <button 
                                          onClick={startBatchSync} 
                                          disabled={isSyncing}
                                          style={{ width: '100%', boxSizing: 'border-box', padding: '18px', background: isSyncing ? '#e5e5ea' : '#007aff', color: isSyncing ? '#86868b' : 'white', border: 'none', borderRadius: '14px', fontSize: '17px', fontWeight: '600', cursor: isSyncing ? 'not-allowed' : 'pointer', transition: 'all 0.2s', position: 'relative', overflow: 'hidden', outline: 'none' }}
                                      >
                                          {isSyncing ? (
                                             <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                                <div className="loader" style={{ width: '18px', height: '18px', marginBottom: 0, border: '3px solid rgba(134,134,139,0.3)', borderTopColor: '#86868b' }}></div>
                                                Batch wird verarbeitet...
                                             </div>
                                          ) : 'Batch Verarbeiten starten'}
                                      </button>
                                  </>
                             )}
                          </div>
                      )}
                  </div>

                  {/* Rotating Tips Footer (Visible during processing) */}
                  {(onboardingStep === 4 && isSyncing) && (
                      <div style={{ position: 'absolute', bottom: '40px', left: '60px', right: '60px', textAlign: 'center', animation: 'fadeIn 0.5s' }}>
                          <p key={currentTipIndex} style={{ margin: 0, fontSize: '15px', color: '#86868b', fontStyle: 'italic', animation: 'fadeIn 1s', lineHeight: '1.5' }}>
                              <span style={{ fontWeight: 'bold', color: '#555', fontStyle: 'normal' }}>💡 DealPin Tipp: </span>
                              {tipsArray[currentTipIndex]}
                          </p>
                      </div>
                  )}
              </div>
          </div>
      );
  }

  if (currentPath.startsWith('/admin')) {
      return <AdminDashboard />;
  }

  if (accessDenied) {
      return (
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', background: '#f5f5f7', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
              <div style={{ background: '#fff', borderRadius: '16px', padding: '40px', textAlign: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', maxWidth: '400px' }}>
                  <ShieldCheck size={48} color="#ff3b30" style={{ marginBottom: '20px' }} />
                  <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px', color: '#1d1d1f' }}>Zugriff verweigert</h2>
                  <p style={{ fontSize: '15px', color: '#86868b', lineHeight: '1.5' }}>Dein HubSpot-Account ist nicht für die Nutzung von DealPin freigeschaltet. Bitte wende dich an deinen Systemadministrator.</p>
              </div>
          </div>
      );
  }

  if (!currentPath.startsWith('/welcome') && !currentPath.startsWith('/admin') && markers.length === 0) {
    return (
      <div className="empty-state">
        <div className="loader"></div>
        <p>Lade Kartendaten...</p>
      </div>
    );
  }

  const initialLat = markers[0].lat;
  const initialLon = markers[0].lon;
  const centerCompany = markers.find(m => m.isCenter) || markers[0];
  const nearbyCompanies = markers.filter(m => !m.isCenter);

  const filteredCompanies = nearbyCompanies.filter(c => {
      // Apply selected filter preset if enabled and set
      if (tenantConfig?.filter_presets_enabled && selectedPreset) {
          if (!applyPresetFilter(c, selectedPreset)) {
              return false;
          }
      }

      // 1. Dynamic Filters from HubSpot Schema
      if (Object.keys(customFilters).length > 0) {
          for (const [key, conf] of Object.entries(customFilters)) {
              if (!conf || !conf.operator) continue;
              
              const confType = conf.type || (tenantConfig?.active_filters || []).find(f => (f.name || f) === key)?.type || 'string';
              
              const val = c.customProps?.[key];
              const safeStrVal = val !== undefined && val !== null ? String(val) : '';
              
              if (conf.operator === 'KNOWN') {
                  if (!safeStrVal) return false;
                  continue;
              }
              if (conf.operator === 'UNKNOWN') {
                  if (safeStrVal) return false;
                  continue;
              }
              
              if (confType === 'number') {
                  const numVal = parseFloat(val);
                  
                  if (conf.operator === 'BETWEEN') {
                      if (!safeStrVal) return false;
                      const minTarget = parseFloat(conf.value);
                      const maxTarget = parseFloat(conf.valueMax);
                      if (!isNaN(minTarget) && numVal < minTarget) return false;
                      if (!isNaN(maxTarget) && numVal > maxTarget) return false;
                  } else {
                      if (!safeStrVal) return false;
                      const targetNum = parseFloat(conf.value);
                      if (isNaN(targetNum)) continue;
                      
                      if (conf.operator === 'EQ' && numVal !== targetNum) return false;
                      if (conf.operator === 'NOT_EQ' && numVal === targetNum) return false;
                      if (conf.operator === 'GT' && numVal <= targetNum) return false;
                      if (conf.operator === 'LT' && numVal >= targetNum) return false;
                  }
              }
              else if (confType === 'date' || confType === 'datetime') {
                  if (!safeStrVal) return false;
                  const targetTime = new Date(safeStrVal).getTime();
                  if (isNaN(targetTime)) return false;
                  
                  if (conf.operator === 'BETWEEN') {
                      const minTime = new Date(conf.value).getTime();
                      const maxTime = new Date(conf.valueMax).getTime();
                      if (!isNaN(minTime) && targetTime < minTime) return false;
                      // Add 1 day to maxTime to include the whole ending day local time
                      if (!isNaN(maxTime) && targetTime > (maxTime + 86399000)) return false;
                  } else {
                      const reqTime = new Date(conf.value).getTime();
                      if (isNaN(reqTime)) continue;
                      
                      const reqObj = new Date(conf.value);
                      const tObj = new Date(safeStrVal);
                      const isSameDay = reqObj.getFullYear() === tObj.getFullYear() && 
                                        reqObj.getMonth() === tObj.getMonth() && 
                                        reqObj.getDate() === tObj.getDate();
                      
                      if (conf.operator === 'EQ' && !isSameDay) return false;
                      // LT is strictly before the day begins
                      if (conf.operator === 'LT' && targetTime >= reqTime) return false;
                      // GT is strictly after the day ends
                      if (conf.operator === 'GT' && targetTime <= (reqTime + 86399000)) return false;
                  }
              }
              else if (confType === 'string') {
                  if (!safeStrVal) return false;
                  const searchStr = String(conf.value || '').toLowerCase();
                  const targetStr = safeStrVal.toLowerCase();
                  
                  if (conf.operator === 'CONTAINS' && !targetStr.includes(searchStr)) return false;
                  if (conf.operator === 'NOT_CONTAINS' && targetStr.includes(searchStr)) return false;
                  if (conf.operator === 'EQ' && targetStr !== searchStr) return false;
                  if (conf.operator === 'NOT_EQ' && targetStr === searchStr) return false;
              }
              else if (confType === 'enumeration' || confType === 'boolean') {
                  const valuesArray = safeStrVal ? safeStrVal.split(';').map(v => v.trim()) : [];
                  const selectedOptions = Array.isArray(conf.value) 
                      ? conf.value 
                      : (conf.value ? [conf.value] : []);
                  if (selectedOptions.length === 0) continue;
                  
                  let hasMatch = false;
                  for (let v of selectedOptions) {
                      if ((v === 'UNKNOWN' || v === 'UNASSIGNED') && valuesArray.length === 0) {
                          hasMatch = true;
                      } else if (valuesArray.includes(String(v))) {
                          hasMatch = true;
                      }
                  }
                  
                  if (conf.operator === 'IN' && !hasMatch) return false;
                  if (conf.operator === 'NOT_IN' && hasMatch) return false;
              }
          }
      }
      return true;
  });

  const rawOwners = [...new Set(nearbyCompanies.map(c => c.ownerId).filter(Boolean))];
  const uniqueOwners = rawOwners.filter(o => o !== 'Unassigned').sort();
  if (rawOwners.includes('Unassigned')) uniqueOwners.unshift('Unassigned');

  const rawTeams = [...new Set(nearbyCompanies.map(c => c.teamId).filter(Boolean))];
  const uniqueTeams = rawTeams.filter(t => t !== 'No Team').sort();
  if (rawTeams.includes('No Team')) uniqueTeams.unshift('No Team');

  const getLabelForFilterValue = (filterDef, rawValue) => {
      if (!rawValue || rawValue === 'UNASSIGNED' || rawValue === 'UNKNOWN') return rawValue === 'UNASSIGNED' ? 'Unassigned' : 'Unbekannt';
      
      // Try built-in schema options first
      const opt = (filterDef.options || []).find(o => String(o.value) === String(rawValue));
      if (opt) return opt.label;

      // Extract dynamically from nearbyCompanies
      if (filterDef.referencedObjectType === 'OWNER' || filterDef.name.includes('owner')) {
          const matchComp = nearbyCompanies.find(c => String(c.customProps?.[filterDef.name]) === String(rawValue));
          if (matchComp) return filterDef.name === 'hubspot_owner_id' ? matchComp.ownerId : matchComp.customProps[filterDef.name];
      }
      return rawValue; // Fallback to raw string
  };

  // Generate interactive circle
  const geojsonCircle = (searchMode === 'radius' && radius) 
      ? createGeoJSONCircle([initialLon, initialLat], radius, 64, circleRotation) 
      : null;



  return (
    <div className="dashboard-container">
      {/* LEFT: 75% MAP AREA */}
      <div className="map-area">
        {toast && (
            <div style={{ position: 'absolute', bottom: '60px', right: '30px', zIndex: 1000, background: toast.type === 'error' ? '#ff3b30' : '#34c759', color: '#fff', padding: '16px 20px', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', fontWeight: '600', animation: 'fadeIn 0.3s ease-out', maxWidth: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', minHeight: '60px' }}>
                {toast.message}
            </div>
        )}
        {error && <div className="floating-error">{error}</div>}
        
        {/* CARTO Positron is an Apple-like minimal light map style built on OSM data */}
        
         {/* GLASSMORPHISM FILTER ACTION STRIP */}
          {/* GLASSMORPHISM FILTER ACTION STRIP */}
          {isSearching && (() => {
              const activeFilterDefs = (tenantConfig?.active_filters || []).filter(filterDef => {
                  const fState = customFilters[filterDef.name];
                  return !!fState?.operator;
              });

              const showAllDirectly = activeFilterDefs.length <= 3;
              const directFilters = showAllDirectly ? activeFilterDefs : activeFilterDefs.slice(0, 2);
              const collapsedFilters = showAllDirectly ? [] : activeFilterDefs.slice(2);

              return (
                  <>
                      {(activeFilter || presetDropdownOpen || addFilterDropdownOpen || moreFiltersDropdownOpen) && (
                          <div onClick={closeAllDropdowns} style={{ position: 'fixed', inset: 0, zIndex: 1 }} />
                      )}
                      
                      <div style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 10, display: 'flex', gap: '10px', alignItems: 'center', width: 'max-content', maxWidth: 'calc(100% - 40px)' }}>
                          {/* Unified Glassmorphic Filter Ribbon */}
                          <div style={{ 
                              background: 'rgba(255,255,255,0.9)', 
                              backdropFilter: 'blur(20px)', 
                              WebkitBackdropFilter: 'blur(20px)', 
                              borderRadius: '20px', 
                              boxShadow: '0 4px 24px rgba(0,0,0,0.08)', 
                              border: '1px solid rgba(255,255,255,0.4)', 
                              padding: '6px 12px', 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px', 
                              zIndex: 2,
                              minHeight: '52px'
                          }}>
                              
                              {/* Presets Button */}
                              {tenantConfig?.filter_presets_enabled && tenantConfig.filter_presets?.length > 0 && (
                                  <div style={{ position: 'relative' }}>
                                      <button 
                                          onClick={togglePresetDropdown}
                                          style={{
                                              background: selectedPreset ? 'rgba(0, 122, 255, 0.1)' : 'transparent',
                                              color: selectedPreset ? '#007aff' : '#1d1d1f',
                                              border: 'none', borderRadius: '10px', padding: '8px 12px', cursor: 'pointer', transition: 'all 0.2s',
                                              display: 'flex', alignItems: 'center', gap: '6px', minHeight: '38px', fontWeight: '600', fontSize: '13.5px'
                                          }}
                                      >
                                          <Filter size={14} />
                                          <span>{selectedPreset ? selectedPreset.name : 'Presets'}</span>
                                          <ChevronDown size={14} />
                                      </button>
                                      
                                      {presetDropdownOpen && (
                                          <div style={{ position: 'absolute', top: '45px', left: 0, background: '#fff', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', border: '1px solid rgba(0,0,0,0.08)', width: '280px', padding: '6px', zIndex: 100, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                              <div style={{ padding: '6px 10px', fontSize: '11px', color: '#86868b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gespeicherte Presets</div>
                                              {tenantConfig.filter_presets.map(p => (
                                                  <button 
                                                      key={p.id}
                                                      onClick={() => {
                                                          setSelectedPreset(selectedPreset?.id === p.id ? null : p);
                                                          closeAllDropdowns();
                                                      }}
                                                      style={{
                                                          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '8px 10px', borderRadius: '8px', border: 'none', background: selectedPreset?.id === p.id ? '#f2f2f7' : 'transparent', textAlign: 'left', cursor: 'pointer', width: '100%', transition: 'background 0.15s'
                                                      }}
                                                      onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f7'}
                                                      onMouseLeave={(e) => e.currentTarget.style.background = selectedPreset?.id === p.id ? '#f2f2f7' : 'transparent'}
                                                  >
                                                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#1d1d1f' }}>{p.name}</span>
                                                      <span style={{ fontSize: '11px', color: '#86868b', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>{p.desc}</span>
                                                  </button>
                                              ))}
                                              {selectedPreset && (
                                                  <button 
                                                      onClick={() => { setSelectedPreset(null); closeAllDropdowns(); }}
                                                      style={{ borderTop: '1px solid #e3e3e8', padding: '8px 10px', fontSize: '12px', color: '#ff3b30', fontWeight: '500', background: 'transparent', width: '100%', textAlign: 'center', cursor: 'pointer', marginTop: '4px' }}
                                                  >
                                                      Preset deaktivieren
                                                  </button>
                                              )}
                                          </div>
                                      )}
                                  </div>
                              )}
                              
                              {/* + Filter Button */}
                              <div style={{ position: 'relative' }}>
                                  <button 
                                      id="filter-btn-add"
                                      onClick={toggleAddFilterDropdown}
                                      style={{
                                          background: 'transparent',
                                          color: '#1d1d1f',
                                          border: 'none', borderRadius: '10px', padding: '8px 12px', cursor: 'pointer', transition: 'all 0.2s',
                                          display: 'flex', alignItems: 'center', gap: '4px', minHeight: '38px', fontWeight: '600', fontSize: '13.5px'
                                      }}
                                  >
                                      <Plus size={14} />
                                      <span>Filter</span>
                                      <ChevronDown size={14} />
                                  </button>
                                  
                                  {addFilterDropdownOpen && (
                                      <div style={{ position: 'absolute', top: '45px', left: 0, background: '#fff', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', border: '1px solid rgba(0,0,0,0.08)', width: '220px', padding: '6px', zIndex: 100, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                          <div style={{ padding: '6px 10px', fontSize: '11px', color: '#86868b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Eigenschaft filtern</div>
                                          {(tenantConfig?.active_filters || []).map(filterDef => {
                                              const fState = customFilters[filterDef.name];
                                              const isFilterActive = !!fState?.operator;
                                              return (
                                                  <button 
                                                      key={filterDef.name}
                                                      onClick={() => {
                                                          toggleActiveFilter(filterDef.name);
                                                      }}
                                                      style={{
                                                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: '8px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', width: '100%', transition: 'background 0.15s'
                                                      }}
                                                      onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f7'}
                                                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                                  >
                                                      <span style={{ fontSize: '13px', fontWeight: isFilterActive ? '600' : '400', color: '#1d1d1f' }}>
                                                          {FILTER_TRANSLATIONS[filterDef.label] || filterDef.label}
                                                      </span>
                                                      {isFilterActive && <Check size={12} style={{ color: '#007aff' }} />}
                                                  </button>
                                              );
                                          })}
                                      </div>
                                  )}
                              </div>
                              
                              {/* Vertical Divider */}
                              {(selectedPreset || activeFilterDefs.length > 0) && (
                                  <div style={{ width: '1px', height: '20px', background: 'rgba(0,0,0,0.1)', margin: '0 4px' }} />
                              )}
                              
                              {/* Active Preset Pill inside the ribbon */}
                              {selectedPreset && (
                                  <div style={{ position: 'relative', display: 'flex' }}>
                                      <button 
                                          onClick={() => { setSelectedPreset(null); closeAllDropdowns(); }}
                                          style={{
                                              background: 'rgba(0, 122, 255, 0.1)',
                                              color: '#007aff',
                                              border: 'none', borderRadius: '10px', padding: '6px 12px', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap',
                                              display: 'flex', alignItems: 'center', gap: '6px', minHeight: '38px', textAlign: 'left', paddingRight: '28px', position: 'relative'
                                          }}
                                          title="Preset deaktivieren"
                                      >
                                          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                              <span style={{ fontSize: '10px', fontWeight: '500', color: 'rgba(0,122,255,0.7)', textTransform: 'uppercase' }}>Preset</span>
                                              <span style={{ fontSize: '13px', fontWeight: '600' }}>{selectedPreset.name}</span>
                                          </div>
                                          <div 
                                              onClick={(e) => { e.stopPropagation(); setSelectedPreset(null); closeAllDropdowns(); }}
                                              style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                          >
                                              <X size={12} style={{ opacity: 0.8 }} />
                                          </div>
                                      </button>
                                  </div>
                              )}
                              
                              {/* Active Property Pills */}
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap', alignItems: 'center' }}>
                                  {directFilters.map(filterDef => {
                                      const fState = customFilters[filterDef.name];
                                      
                                      let valText = '';
                                      if (fState.operator === 'KNOWN') valText = 'ist bekannt';
                                      else if (fState.operator === 'UNKNOWN') valText = 'ist unbekannt';
                                      else if (Array.isArray(fState.value)) valText = fState.value.map(v => getLabelForFilterValue(filterDef, v)).join(', ');
                                      else valText = getLabelForFilterValue(filterDef, fState.value);
                                      
                                      let operatorSymbol = '';
                                      if (fState.operator === 'EQ' || fState.operator === 'IN') operatorSymbol = ':';
                                      else if (fState.operator === 'NOT_EQ' || fState.operator === 'NOT_IN') operatorSymbol = ' !=';
                                      else if (fState.operator === 'GT') operatorSymbol = ' >';
                                      else if (fState.operator === 'LT') operatorSymbol = ' <';
                                      else if (fState.operator === 'CONTAINS') operatorSymbol = ' ~';
                                      else if (fState.operator === 'NOT_CONTAINS') operatorSymbol = ' !~';
                                      else if (fState.operator === 'BETWEEN') operatorSymbol = ' :';
                                      
                                      let displayValue = valText;
                                      if (fState.operator === 'BETWEEN') {
                                          let val1 = fState.value || '';
                                          let val2 = fState.valueMax || '';
                                          if (filterDef.type === 'date' || filterDef.type === 'datetime') {
                                              val1 = val1 ? new Date(val1).toLocaleDateString() : '';
                                              val2 = val2 ? new Date(val2).toLocaleDateString() : '';
                                          }
                                          displayValue = `${val1} - ${val2}`;
                                      } else if ((filterDef.type === 'date' || filterDef.type === 'datetime') && fState.value) {
                                          displayValue = new Date(fState.value).toLocaleDateString();
                                      }
                                      
                                      const label = FILTER_TRANSLATIONS[filterDef.label] || filterDef.label;
                                      
                                      return (
                                          <div key={filterDef.name} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                              <button 
                                                  id={'filter-btn-' + filterDef.name}
                                                  onClick={() => toggleActiveFilter(filterDef.name)}
                                                  style={{
                                                      background: activeFilter === filterDef.name ? '#007aff' : '#f2f2f7',
                                                      color: activeFilter === filterDef.name ? '#fff' : '#1d1d1f',
                                                      border: 'none', borderRadius: '10px', padding: '6px 12px', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap',
                                                      display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '38px', paddingRight: '28px', textAlign: 'left'
                                                  }}
                                              >
                                                  <span style={{ fontSize: '11px', fontWeight: '500', color: activeFilter === filterDef.name ? 'rgba(255,255,255,0.7)' : '#86868b' }}>
                                                      {label}
                                                  </span>
                                                  <span style={{ fontSize: '12px', fontWeight: '600' }}>
                                                      {fState.operator === 'KNOWN' || fState.operator === 'UNKNOWN' ? valText : `${operatorSymbol} ${displayValue}`}
                                                  </span>
                                              </button>
                                              <button 
                                                  onClick={(e) => {
                                                      e.stopPropagation();
                                                      const newFilters = { ...customFilters };
                                                      delete newFilters[filterDef.name];
                                                      setCustomFilters(newFilters);
                                                      if (activeFilter === filterDef.name) setActiveFilter(null);
                                                  }}
                                                  style={{
                                                      position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
                                                      background: 'transparent', border: 'none', cursor: 'pointer',
                                                      color: activeFilter === filterDef.name ? 'rgba(255,255,255,0.8)' : '#86868b',
                                                      padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                  }}
                                                  title="Filter löschen"
                                              >
                                                  <X size={12} />
                                              </button>
                                          </div>
                                      );
                                  })}

                                  {/* Collapsed "+ X more" active filters button & dropdown */}
                                  {collapsedFilters.length > 0 && (
                                      <div style={{ position: 'relative', display: 'flex' }}>
                                          <button 
                                              id="filter-btn-more"
                                              onClick={toggleMoreFiltersDropdown}
                                              style={{
                                                  background: moreFiltersDropdownOpen ? '#007aff' : '#f2f2f7',
                                                  color: moreFiltersDropdownOpen ? '#fff' : '#1d1d1f',
                                                  border: 'none', borderRadius: '10px', padding: '8px 12px', cursor: 'pointer', transition: 'all 0.2s',
                                                  display: 'flex', alignItems: 'center', gap: '6px', minHeight: '38px', fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap'
                                              }}
                                          >
                                              <span>+ {collapsedFilters.length} weitere</span>
                                              <ChevronDown size={14} style={{ color: moreFiltersDropdownOpen ? '#fff' : '#1d1d1f' }} />
                                          </button>
                                          
                                          {moreFiltersDropdownOpen && (
                                              <div style={{ position: 'absolute', top: '45px', left: 0, background: '#fff', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', border: '1px solid rgba(0,0,0,0.08)', width: '260px', padding: '6px', zIndex: 100, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                  <div style={{ padding: '6px 10px', fontSize: '11px', color: '#86868b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Weitere active Filter</div>
                                                  {collapsedFilters.map(filterDef => {
                                                      const fState = customFilters[filterDef.name];
                                                      const label = FILTER_TRANSLATIONS[filterDef.label] || filterDef.label;
                                                      
                                                      let valText = '';
                                                      if (fState.operator === 'KNOWN') valText = 'ist bekannt';
                                                      else if (fState.operator === 'UNKNOWN') valText = 'ist unbekannt';
                                                      else if (Array.isArray(fState.value)) valText = fState.value.map(v => getLabelForFilterValue(filterDef, v)).join(', ');
                                                      else valText = getLabelForFilterValue(filterDef, fState.value);
                                                      
                                                      let operatorSymbol = '';
                                                      if (fState.operator === 'EQ' || fState.operator === 'IN') operatorSymbol = ':';
                                                      else if (fState.operator === 'NOT_EQ' || fState.operator === 'NOT_IN') operatorSymbol = ' !=';
                                                      else if (fState.operator === 'GT') operatorSymbol = ' >';
                                                      else if (fState.operator === 'LT') operatorSymbol = ' <';
                                                      else if (fState.operator === 'CONTAINS') operatorSymbol = ' ~';
                                                      else if (fState.operator === 'NOT_CONTAINS') operatorSymbol = ' !~';
                                                      else if (fState.operator === 'BETWEEN') operatorSymbol = ' :';

                                                      let displayValue = valText;
                                                      if (fState.operator === 'BETWEEN') {
                                                          let val1 = fState.value || '';
                                                          let val2 = fState.valueMax || '';
                                                          if (filterDef.type === 'date' || filterDef.type === 'datetime') {
                                                              val1 = val1 ? new Date(val1).toLocaleDateString() : '';
                                                              val2 = val2 ? new Date(val2).toLocaleDateString() : '';
                                                          }
                                                          displayValue = `${val1} - ${val2}`;
                                                      } else if ((filterDef.type === 'date' || filterDef.type === 'datetime') && fState.value) {
                                                          displayValue = new Date(fState.value).toLocaleDateString();
                                                      }

                                                      const valDisplay = fState.operator === 'KNOWN' || fState.operator === 'UNKNOWN' ? valText : `${operatorSymbol} ${displayValue}`;

                                                      return (
                                                          <div key={filterDef.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderRadius: '8px', background: '#f5f5f7', transition: 'background 0.2s' }}>
                                                              <button
                                                                  onClick={() => {
                                                                      toggleActiveFilter(filterDef.name);
                                                                  }}
                                                                  style={{
                                                                      border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', flex: 1, padding: 0, display: 'flex', flexDirection: 'column'
                                                                  }}
                                                              >
                                                                  <span style={{ fontSize: '10.5px', color: '#86868b', fontWeight: '500' }}>{label}</span>
                                                                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#1d1d1f', marginTop: '1px' }}>{valDisplay}</span>
                                                              </button>
                                                              <button
                                                                  onClick={(e) => {
                                                                      e.stopPropagation();
                                                                      const newFilters = { ...customFilters };
                                                                      delete newFilters[filterDef.name];
                                                                      setCustomFilters(newFilters);
                                                                      if (activeFilter === filterDef.name) setActiveFilter(null);
                                                                  }}
                                                                  style={{
                                                                      background: 'transparent', border: 'none', cursor: 'pointer', color: '#86868b', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'color 0.2s'
                                                                  }}
                                                                  onMouseEnter={(e) => e.currentTarget.style.color = '#ff3b30'}
                                                                  onMouseLeave={(e) => e.currentTarget.style.color = '#86868b'}
                                                              >
                                                                  <X size={12} />
                                                              </button>
                                                          </div>
                                                      );
                                                  })}
                                              </div>
                                          )}
                                      </div>
                                  )}
                              </div>
                              
                              {/* Clear All Pill inside the ribbon */}
                              {(activeFilterDefs.length > 0 || selectedPreset) && (
                                  <>
                                      <div style={{ width: '1px', height: '20px', background: 'rgba(0,0,0,0.1)', margin: '0 4px' }} />
                                      <button 
                                          onClick={(e) => {
                                              e.stopPropagation();
                                              setCustomFilters({});
                                              setSelectedPreset(null);
                                              setActiveFilter(null);
                                              closeAllDropdowns();
                                          }}
                                          style={{
                                             background: 'rgba(255,59,48,0.1)',
                                             color: '#ff3b30',
                                             border: 'none', borderRadius: '10px', padding: '8px 12px', minHeight: '38px', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap',
                                             display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '600'
                                          }}
                                      >
                                          <span>Alle löschen</span>
                                      </button>
                                  </>
                              )}
                          </div>
                      </div>

                      {activeFilter && (
                          <div style={{ ...popupStyle, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: '16px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', border: '1px solid rgba(255,255,255,0.4)', padding: '20px', width: '360px', animation: 'fadeIn 0.2s ease-out', textAlign: 'left' }}>
                              <FilterPopupContent 
                                 activeDef={(tenantConfig?.active_filters || []).find(f => f.name === activeFilter)} 
                                 customFilters={customFilters} 
                                 setCustomFilters={setCustomFilters} 
                                 nearbyCompanies={nearbyCompanies} 
                                 FILTER_TRANSLATIONS={FILTER_TRANSLATIONS} 
                              />
                          </div>
                      )}
                  </>
              );
          })()}

        <Map
          onClick={(e) => {
              if (e.defaultPrevented) return;
              setFocusedCompanyIds([]);
              setIsDropdownOpen(false);
          }}
          initialViewState={{
            longitude: initialLon,
            latitude: initialLat,
            zoom: searchMode === 'radius' && radius > 50 ? 9 : 11
          }}
          attributionControl={false}
          mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
          style={{ width: '100%', height: '100%' }}
        >
          <NavigationControl position="bottom-right" />
          
          {/* RADIUS CIRCLE */}
          {geojsonCircle && (
            <Source id="my-data" type="geojson" data={geojsonCircle}>
              <Layer
                id="polygon-fill"
                type="fill"
                paint={{
                  'fill-color': '#007aff',
                  'fill-opacity': 0.08
                }}
              />
              <Layer
                id="polygon-outline"
                type="line"
                paint={{
                  'line-color': '#007aff',
                  'line-width': 2,
                  'line-dasharray': [2, 2]
                }}
              />
            </Source>
          )}
          
          {(() => {
            const allMarkers = [centerCompany, ...filteredCompanies];
            const clusterMap = {};
            const clusteredMarkers = [];
            
            allMarkers.forEach(m => {
                if (!m.lat || !m.lon) return;
                const key = `${m.lat}-${m.lon}`;
                if(!clusterMap[key]) clusterMap[key] = [];
                clusterMap[key].push(m);
            });
            
            Object.values(clusterMap).forEach(group => {
                clusteredMarkers.push({
                    lat: group[0].lat,
                    lon: group[0].lon,
                    isCenter: group.some(m => m.isCenter),
                    count: group.length,
                    companies: group
                });
            });

            return clusteredMarkers.map((cluster, idx) => {
                const isImprecise = !cluster.companies.some(c => c.address) && !cluster.isCenter;
                const isRoutingDisabled = selectionMode === 'routing' && isImprecise;
                const isLead = cluster.companies.some(c => c.isLead);
                    let markerClass = isLead ? 'lead-marker' : 'nearby-marker';
                    if (cluster.companies.some(c => selectedIds.includes(c.id)) || cluster.companies.some(c => focusedCompanyIds.includes(c.id))) {
                        markerClass = 'focused-marker';
                    } else if (cluster.isCenter) {
                        markerClass = 'center-marker';
                    }

                    return (
                        <Marker 
                            key={idx} 
                            longitude={cluster.lon} 
                            latitude={cluster.lat} 
                            anchor="center"
                            onClick={e => {
                                e.originalEvent.stopPropagation();
                                if (isRoutingDisabled) return; // Prevent selection logic entirely
                                if (selectionMode) {
                                    let idsInCluster = cluster.companies.map(c => c.id);
                                    if (selectionMode === 'routing') {
                                        idsInCluster = cluster.companies.filter(c => c.address || c.isCenter).map(c => c.id);
                                        if (idsInCluster.length === 0) return; // Prevent selection if no valid address exists
                                    }
                                    setSelectedIds(prev => {
                                        const allSelected = idsInCluster.every(id => prev.includes(id));
                                        if (allSelected) {
                                            return prev.filter(id => !idsInCluster.includes(id));
                                        } else {
                                            return [...new Set([...prev, ...idsInCluster])];
                                        }
                                    });
                                } else {
                                    const idsInCluster = cluster.companies.map(c => c.id);
                                    setFocusedCompanyIds(idsInCluster);
                                    setTimeout(() => {
                                        const firstCardEl = document.getElementById(`company-card-${idsInCluster[0]}`);
                                        if (firstCardEl) firstCardEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    }, 50);
                                }
                            }}
                        >
                            {(() => {
                                const hasOpenDeals = cluster.companies.some(c => (c.openDeals || 0) > 0);
                                const shouldPulse = tenantConfig?.pulse_pins_enabled || 
                                                    (tenantConfig?.pulse_only_active_deals && hasOpenDeals);
                                const showPulse = shouldPulse && !isRoutingDisabled;

                                return (
                                    <div 
                                        className={`modern-marker ${markerClass}`} 
                                        style={{
                                            ...(cluster.count > 1 ? { width: '24px', height: '24px' } : {}),
                                            ...(isImprecise ? { filter: 'grayscale(1)', opacity: isRoutingDisabled ? 0.4 : 0.7, cursor: isRoutingDisabled ? 'not-allowed' : 'pointer' } : {}),
                                            ...(isLead && !cluster.isCenter ? { backgroundColor: '#8a2be2', border: '2px solid #fff' } : {}) // Distinct styles for leads
                                        }}
                                    >
                                        {showPulse && (
                                            <div 
                                                className="pulse" 
                                                style={{ 
                                                    animationDelay: `-${Math.abs(cluster.lon * 100) % 2}s`, 
                                                    ...(cluster.count > 1 ? { animationDuration: '3s', transform: 'scale(1.5)' } : {}),
                                                    ...(isLead ? { backgroundColor: '#8a2be2' } : {})
                                                }}
                                            />
                                        )}
                                        <div 
                                            className="core" 
                                            style={{
                                                ...(cluster.count > 1 ? { display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '11px', fontWeight: 'bold', width: '24px', height: '24px', minWidth: '24px', minHeight: '24px', flexShrink: 0, borderRadius: '50%', padding: 0 } : {}),
                                                ...(isLead ? { backgroundColor: '#8a2be2' } : {})
                                            }}
                                        >
                                            {cluster.count > 1 ? cluster.count : ''}
                                        </div>
                                    </div>
                                );
                            })()}
                        </Marker>
                    );
                });
            })()}

          {/* Popup Logic Removed as requested by the user, replaced with List Focusing */}
        </Map>
        
        {/* Action Bar Overlays */}
        {!isScreenshotMode && filteredCompanies.length > 0 && (
            <div style={{ position: 'absolute', bottom: '30px', left: '30px', zIndex: 10, display: 'inline-flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', gap: '10px', background: 'rgba(255,255,255,0.9)', padding: '8px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.4)', alignItems: 'center' }}>
                    <button onClick={() => { setSelectionMode(selectionMode === 'segments' ? null : 'segments'); setSelectedIds([]); setFocusedCompanyIds([]); setSegmentSearch(''); setIsDropdownOpen(false); }} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: selectionMode === 'segments' ? '#1d1d1f' : 'transparent', color: selectionMode === 'segments' ? '#fff' : '#1d1d1f', cursor: 'pointer', fontWeight: '600', fontSize: '13px', transition: 'all 0.2s' }}>Segmente</button>
                    <button onClick={() => { setSelectionMode(selectionMode === 'routing' ? null : 'routing'); setSelectedIds([]); setFocusedCompanyIds([]); setSegmentSearch(''); setIsDropdownOpen(false); }} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: selectionMode === 'routing' ? '#1d1d1f' : 'transparent', color: selectionMode === 'routing' ? '#fff' : '#1d1d1f', cursor: 'pointer', fontWeight: '600', fontSize: '13px', transition: 'all 0.2s' }}>Routenplanung</button>
                    {selectionMode && (
                        <>
                           <div style={{ width: '1px', height: '20px', background: '#d1d1d6', margin: '0 5px' }}></div>
                           <button onClick={() => { setSelectionMode(null); setSelectedIds([]); setFocusedCompanyIds([]); setSegmentSearch(''); setIsDropdownOpen(false); }} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #ff3b30', background: '#ffe5e5', color: '#ff3b30', cursor: 'pointer', fontWeight: '600', fontSize: '12px', transition: 'all 0.2s' }}>Abbrechen</button>
                        </>
                    )}
                </div>
                
                {selectionMode && selectedIds.length > 0 && (
                    <div style={{ background: 'rgba(255,255,255,0.95)', padding: '15px', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.15)', width: '100%', boxSizing: 'border-box', animation: 'fadeIn 0.2s ease-out' }}>
                        <h3 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>{selectedIds.length} Unternehmen ausgewählt</h3>
                        {selectionMode === 'routing' ? (
                            <button onClick={handleGenerateRoute} style={{ width: '100%', padding: '10px', background: '#007aff', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>Route in Google Maps öffnen</button>
                        ) : (
                            <div style={{ display: 'flex', gap: '8px', position: 'relative' }}>
                                <input 
                                    type="text" 
                                    placeholder="Segment suchen / auswählen..."
                                    value={segmentSearch}
                                    onChange={(e) => { setSegmentSearch(e.target.value); setIsDropdownOpen(true); }}
                                    onFocus={() => { setIsDropdownOpen(true); }}
                                    onBlur={() => setTimeout(() => setIsDropdownOpen(false), 200)}
                                    style={{ flex: 1, padding: '10px 14px', border: '1px solid #d1d1d6', borderRadius: '8px', boxSizing: 'border-box', fontSize: '13px', outline: 'none', backgroundColor: '#fff', color: '#1d1d1f' }}
                                />
                                <button 
                                    onClick={() => {
                                        const match = availableLists.find(l => l.name.toLowerCase() === segmentSearch.toLowerCase().trim());
                                        if (match) addCompaniesToSegment(match.listId);
                                        else if (segmentSearch.trim() !== '') createNewSegment(segmentSearch.trim());
                                    }}
                                    disabled={segmentSearch.trim() === ''}
                                    style={{ padding: '0 16px', background: segmentSearch.trim() === '' ? '#e5e5ea' : '#007aff', color: segmentSearch.trim() === '' ? '#86868b' : '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: segmentSearch.trim() === '' ? 'not-allowed' : 'pointer', fontSize: '13px', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
                                >
                                    {availableLists.some(l => l.name.toLowerCase() === segmentSearch.toLowerCase().trim()) ? 'Hinzufügen' : 'Erstellen'}
                                </button>
                                
                                {isDropdownOpen && (
                                    <div style={{ position: 'absolute', bottom: '100%', left: 0, width: '100%', background: '#fff', border: '1px solid #d1d1d6', borderRadius: '8px', boxShadow: '0 -4px 12px rgba(0,0,0,0.1)', marginBottom: '5px', maxHeight: '200px', overflowY: 'auto', zIndex: 20 }}>
                                        {(() => {
                                            const filtered = availableLists.filter(l => l.name.toLowerCase().includes(segmentSearch.toLowerCase()));
                                            const exactMatch = availableLists.some(l => l.name.toLowerCase() === segmentSearch.toLowerCase().trim());
                                            
                                            return (
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    {filtered.map(l => (
                                                        <button key={l.listId} onClick={() => { setSegmentSearch(l.name); setIsDropdownOpen(false); }} style={{ padding: '10px 14px', textAlign: 'left', backgroundColor: '#fff', color: '#1d1d1f', border: 'none', borderBottom: '1px solid #f2f2f7', cursor: 'pointer', fontSize: '13px', width: '100%' }}>
                                                            {l.name}
                                                        </button>
                                                    ))}
                                                    {filtered.length === 0 && segmentSearch.trim() !== '' && !exactMatch && (
                                                        <div style={{ padding: '10px 14px', fontSize: '13px', color: '#86868b', textAlign: 'center', backgroundColor: '#fff' }}>Als neues Segment erstellen...</div>
                                                    )}
                                                    {filtered.length === 0 && segmentSearch.trim() === '' && (
                                                        <div style={{ padding: '10px 14px', fontSize: '13px', color: '#86868b', textAlign: 'center', backgroundColor: '#fff' }}>Keine Segmente gefunden.</div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        )}
      </div>

      {/* RIGHT: 25% SIDEBAR AREA */}
      <div className="sidebar-area glass-panel" style={{ background: isScreenshotMode ? '#ffffff' : undefined, backdropFilter: isScreenshotMode ? 'none' : undefined, WebkitBackdropFilter: isScreenshotMode ? 'none' : undefined }}>
        
        {!isSearching ? (
          <>
            <div className="nearby-card center-location-card" style={{ background: '#fff', borderRadius: '12px', padding: '14px', boxShadow: '0 4px 16px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.03)', marginBottom: '1.5rem', marginTop: '10px' }}>
                 <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px', position: 'relative' }}>
                    <strong style={{ display: 'block', fontSize: '14px', color: '#1d1d1f', textAlign: 'left', paddingRight: '30px' }}>{centerCompany.name || 'Unbekannt'}</strong>
                    <a href={`https://app.hubspot.com/contacts/${activePortalId || 'demo'}/record/0-2/${centerCompany.id}`} target="_blank" rel="noopener noreferrer" style={{ position: 'absolute', right: '0', top: '0', color: '#86868b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px', background: '#f5f5f7', borderRadius: '6px' }} title="In HubSpot öffnen">
                        <ExternalLink size={14} />
                    </a>
                    <span style={{ display: 'block', fontSize: '12px', color: '#86868b', textAlign: 'left' }}>
                       {centerCompany.address && `${centerCompany.address}, `}{centerCompany.zip} {centerCompany.city} {centerCompany.country && `(${centerCompany.country})`}
                    </span>
                 </div>
                 <div style={{ display: 'flex', gap: '15px', borderTop: '1px solid rgba(0,0,0,0.04)', paddingTop: '10px', marginTop: '4px' }}>
                    <div style={{ fontSize: '11px', color: '#86868b' }}>
                       <strong style={{ color: '#1d1d1f' }}>{centerCompany.employees || '-'}</strong> Employees
                    </div>
                    <div style={{ fontSize: '11px', color: '#86868b' }}>
                       <strong style={{ color: '#1d1d1f' }}>{centerCompany.annualRevenue ? `€${centerCompany.annualRevenue.toLocaleString('de-DE')}` : '-'}</strong> Sales
                    </div>
                 </div>
                 <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px', fontSize: '11px', color: '#555' }}>
                     <span style={{ background: '#f5f5f7', padding: '4px 8px', borderRadius: '4px' }}><strong>Owner:</strong> {centerCompany.ownerId || 'Unassigned'}</span>
                     <span style={{ background: '#f5f5f7', padding: '4px 8px', borderRadius: '4px' }}><strong>Deals:</strong> {centerCompany.openDeals || 0} Open / {centerCompany.closedDeals || 0} Closed</span>
                 </div>
            </div>

            <div className="search-controls" style={{ background: '#f5f5f7', padding: '15px', borderRadius: '12px', marginBottom: '1.5rem' }}>
               <div style={{ display: 'flex', background: '#e3e3e8', padding: '3px', borderRadius: '8px', marginBottom: '20px', position: 'relative' }}>
                  <div style={{ flex: 1, textAlign: 'center', padding: '6px 0', fontSize: '12px', fontWeight: searchMode === 'radius' ? '600' : '500', color: searchMode === 'radius' ? '#1d1d1f' : '#86868b', background: searchMode === 'radius' ? '#fff' : 'transparent', borderRadius: '6px', boxShadow: searchMode === 'radius' ? '0 2px 4px rgba(0,0,0,0.06)' : 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1, transition: 'all 0.2s ease', whiteSpace: 'nowrap' }} onClick={() => !loading && setSearchMode('radius')}>Umkreis</div>
                  <div style={{ flex: 1, textAlign: 'center', padding: '6px 0', fontSize: '12px', fontWeight: searchMode === 'zip' ? '600' : '500', color: searchMode === 'zip' ? '#1d1d1f' : '#86868b', background: searchMode === 'zip' ? '#fff' : 'transparent', borderRadius: '6px', boxShadow: searchMode === 'zip' ? '0 2px 4px rgba(0,0,0,0.06)' : 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1, transition: 'all 0.2s ease', whiteSpace: 'nowrap' }} onClick={() => !loading && setSearchMode('zip')}>PLZ-Gebiet</div>
              </div>

              {searchMode === 'radius' ? (
                  <div style={{ marginBottom: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#86868b', marginBottom: '8px' }}>
                          <span>Distanz (Luftlinie)</span>
                          <strong style={{ color: '#007aff' }}>{radius} km</strong>
                      </div>
                      <input 
                          type="range" className="apple-slider" min="1" max="50" 
                          value={radius} onChange={(e) => setRadius(parseInt(e.target.value))}
                          disabled={loading}
                          style={{ 
                              width: '100%', cursor: loading ? 'not-allowed' : 'pointer', marginTop: '5px',
                              opacity: loading ? 0.5 : 1,
                              background: `linear-gradient(to right, #007aff 0%, #007aff ${((radius - 1) / 49) * 100}%, #e3e3e8 ${((radius - 1) / 49) * 100}%, #e3e3e8 100%)`
                          }}
                      />
                  </div>
              ) : (
                  <div style={{ marginBottom: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#86868b', marginBottom: '8px' }}>
                          <span>PLZ-Übereinstimmung</span>
                          <strong>{zipLength} Stellen</strong>
                      </div>
                      <input 
                          type="range" className="apple-slider" min="1" max="5" 
                          value={zipLength} onChange={(e) => setZipLength(parseInt(e.target.value))}
                          disabled={loading}
                          style={{ 
                              width: '100%', cursor: loading ? 'not-allowed' : 'pointer', marginTop: '5px',
                              opacity: loading ? 0.5 : 1,
                              background: `linear-gradient(to right, #007aff 0%, #007aff ${((zipLength - 1) / 4) * 100}%, #e3e3e8 ${((zipLength - 1) / 4) * 100}%, #e3e3e8 100%)`
                          }}
                      />
                      <div style={{ marginTop: '10px', padding: '10px', background: '#fff', borderRadius: '8px', textAlign: 'center', fontSize: '20px', letterSpacing: '4px' }}>
                          <span style={{ fontWeight: '700', color: '#007aff' }}>
                              {centerCompany.zip.substring(0, zipLength)}
                          </span>
                          <span style={{ color: '#000', opacity: 0.15 }}>
                              {centerCompany.zip.substring(zipLength)}
                          </span>
                      </div>
                      <p style={{ fontSize: '11px', color: '#86868b', marginTop: '8px', textAlign: 'center' }}>
                         Findet alle Firmen, deren PLZ mit den blauen Ziffern beginnt.
                      </p>
                  </div>
              )}

              <button id="auto-search-trigger" className="premium-button" onClick={handleLoadNearby} disabled={loading} style={{ marginTop: '15px' }}>
                {loading ? <span>Suche läuft<span className="loading-dots"></span></span> : 'Jetzt Suchen'}
              </button>
            </div>
          </>
        ) : (
          // --- STATE B: RESULTS VIEW ---
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
               <h2 style={{ fontSize: '16px', margin: '0', color: '#007aff', fontWeight: '600', textAlign: 'left' }}>
                  {searchMode === 'radius' ? `Umkreis ${radius}km` : `PLZ-Gebiet ${centerCompany.zip.substring(0, zipLength)}${'x'.repeat(5 - zipLength)}`}
               </h2>
               <button 
                  onClick={() => { setIsSearching(false); setMarkers([{...centerCompany}]); }} 
                  style={{ background: 'none', border: 'none', color: '#007aff', fontWeight: '600', fontSize: '16px', cursor: 'pointer', padding: '0' }}
               >
                  Neue Suche
               </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', marginRight: '-10px', paddingRight: '10px' }}>
               <div className="nearby-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {[centerCompany, ...filteredCompanies].map((m, i) => {
                     const isCenterNode = m.isCenter;
                     const isSelected = selectedIds.includes(m.id);
                     const isImprecise = !m.address && !isCenterNode;
                     const isRoutingDisabled = selectionMode === 'routing' && isImprecise;
                     const isFocused = focusedCompanyIds.includes(m.id);

                     let borderCss = isImprecise ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(0,0,0,0.03)';
                     if (isCenterNode) borderCss = '2px solid #34c759';
                     if (isFocused) borderCss = '2px solid #ff9500';
                     if (isSelected) borderCss = '2px solid #ff9500';
                     
                     let bgCss = isCenterNode ? '#f2fcf4' : (isImprecise ? '#f9f9fb' : '#fff');
                     if (isSelected || isFocused) bgCss = '#fff5e6';
                     if (m.isLead && !isCenterNode) bgCss = '#f9f0ff'; // Light purple for leads
                     
                     const card = (
                     <div id={`company-card-${m.id}`} key={m.id || i} className="nearby-card" onClick={() => {
                        if (isRoutingDisabled) return; // Block selection
                        if (selectionMode) {
                           setSelectedIds(prev => prev.includes(m.id) ? prev.filter(id => id !== m.id) : [...prev, m.id]);
                        } else {
                           setFocusedCompanyIds([m.id]);
                        }
                     }} style={{
                         background: bgCss, 
                         borderRadius: '12px', padding: '14px', cursor: isRoutingDisabled ? 'not-allowed' : 'pointer',
                         opacity: isRoutingDisabled ? 0.4 : (isImprecise ? 0.7 : 1),
                         boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: borderCss,
                         transition: 'all 0.2s ease', filter: isImprecise && !isSelected && !isFocused ? 'grayscale(0.6)' : 'none'
                     }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px', position: 'relative' }}>
                           <strong style={{ display: 'block', fontSize: '14px', color: '#1d1d1f', textAlign: 'left', paddingRight: '30px' }}>
                              {m.name} 
                              {m.isLead && <span style={{ marginLeft: '6px', fontSize: '10px', background: '#8a2be2', color: '#fff', borderRadius: '4px', padding: '2px 4px', verticalAlign: 'middle' }}>NEUER LEAD</span>}
                           </strong>
                           {!isRoutingDisabled && m.id && !m.isLead && (
                               <a onClick={(e) => e.stopPropagation()} href={`https://app.hubspot.com/contacts/${activePortalId || 'demo'}/record/0-2/${m.id}`} target="_blank" rel="noopener noreferrer" style={{ position: 'absolute', right: '0', top: '0', color: '#86868b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px', background: '#f5f5f7', borderRadius: '6px' }} title="In HubSpot öffnen">
                                   <ExternalLink size={14} />
                               </a>
                           )}
                           <span style={{ display: 'block', fontSize: '12px', color: '#86868b', textAlign: 'left' }}>
                              {m.address && `${m.address}, `}{m.zip} {m.city} {m.country && `(${m.country})`}
                           </span>
                        </div>
                        <div style={{ display: 'flex', gap: '15px', borderTop: '1px solid rgba(0,0,0,0.04)', paddingTop: '10px', marginTop: '4px' }}>
                           <div style={{ fontSize: '11px', color: '#86868b' }}>
                              <strong style={{ color: '#1d1d1f' }}>{m.employees || '-'}</strong> Employees
                           </div>
                           <div style={{ fontSize: '11px', color: '#86868b' }}>
                              <strong style={{ color: '#1d1d1f' }}>{m.annualRevenue ? `€${m.annualRevenue.toLocaleString('de-DE')}` : '-'}</strong> Sales
                           </div>
                           {searchMode === 'radius' && m.distanceKm !== undefined && (
                           <div style={{ fontSize: '11px', color: '#007aff', marginLeft: 'auto', fontWeight: '500' }}>
                              {m.distanceKm.toFixed(1)} km
                           </div>
                           )}
                        </div>
                        {m.isLead ? (
                            <div style={{ marginTop: '12px' }}>
                                <button 
                                   onClick={(e) => {
                                       e.stopPropagation();
                                       showToast('Lead wurde erfolgreich im HubSpot CRM angelegt!', 'success');
                                       setMarkers(prev => prev.filter(marker => marker.id !== m.id));
                                   }} 
                                   style={{ width: '100%', padding: '10px', background: '#8a2be2', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.2s', boxShadow: '0 2px 6px rgba(138, 43, 226, 0.3)' }}
                                >
                                    + Ins CRM importieren
                                </button>
                            </div>
                        ) : (
                           <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px', fontSize: '11px', color: '#555' }}>
                               <span style={{ background: '#f5f5f7', padding: '4px 8px', borderRadius: '4px' }}><strong>Owner:</strong> {m.ownerId}</span>
                               <span style={{ background: '#f5f5f7', padding: '4px 8px', borderRadius: '4px' }}><strong>Deals:</strong> {m.openDeals} Open / {m.closedDeals} Closed</span>
                            </div>
                        )}
                       </div>
                     );
                     
                     if (i === 1) {
                         return (
                             <React.Fragment key={m.id || i + '_wrap'}>
                                 <div className="list-header" style={{ marginTop: '10px', marginBottom: '5px' }}>
                                    <span className="section-label">Gefundene Firmen</span>
                                    <span className="badge">{filteredCompanies.length}</span>
                                 </div>
                                 {card}
                             </React.Fragment>
                         );
                     }
                     return card;
                  })}
                  {filteredCompanies.length === 0 && (
                      <React.Fragment>
                         <div className="list-header" style={{ marginTop: '10px', marginBottom: '5px' }}>
                            <span className="section-label">Gefundene Firmen</span>
                            <span className="badge">0</span>
                         </div>
                         <div className="empty-list" style={{ textAlign: 'center', padding: '30px 10px', color: '#86868b', fontSize: '13px' }}>Keine Treffer für diese Suchparameter gefunden.</div>
                      </React.Fragment>
                  )}
               </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default App;
